// TemplateWrapper - WRAPPER for all document types with advanced features
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandVatRate, getBrandLocale } from '../../../../shared/utils/brandCurrency';
import { getBrandDefaultCountry } from '../../../../shared/utils/brandCountry';
import { isSaudiBrand } from '../../../../products/saudi/utils/isSaudiBrand';
import { ArrowLeft, Download, Save } from 'lucide-react';
import { useSettings } from '../../../../core/contexts/SettingsContext';
import { useProducts } from '../../../../core/contexts/ProductsContext';
import { useClients } from '../../../../core/contexts/ClientsContext';
import {
  getFacturationVatMode,
  getClientTypePreferentialRate,
  computeVatBreakdown,
} from '../../facturationConfigDefaults';
import { generateDocumentPDF, downloadDocumentPDF } from '../../../../shared/utils/pdfGenerator';
import {
  decodePaymentVoucherAccountsPayerContact,
  resolveExplicitOriginalInvoiceDate,
  buildDocumentPartnerSectionLabel,
  RECEPTION_INDICATIVE_HT_BOX_LABEL,
  normalizeReceptionLineItem,
  sumReceptionIndicativeHT,
} from '../../../../shared/utils/documentTypeUtils';
import { getTemplateComponent } from './TemplateRegistry';
import {
  PREVIEW_LABELS,
  SUPPLEMENTARY_SECTIONS,
  formatPreviewDate,
  formatPreviewPaymentMethod,
  formatPreviewPriority,
  getDocumentDetailsLabelForType,
  getDocumentPartnerLabelForType,
  getSupplementarySectionPreviewLabel,
  getSupplementarySectionShowField,
  getTotalsRowLabels,
  normalizeSupplementarySectionOrder,
  translatePartnerSectionLabel,
  translatePreviewRowLabel,
  translatePreviewSectionTitle,
  formatPreviewPaymentVoucherReason,
  mapPreviewEnumValue,
  PREVIEW_ENUM_VALUES,
} from '../../../../shared/i18n/documentPreviewLabels';

/**
 * Unifie client / fournisseur pour les factures (aperçu + PDF) : libellé facture **NIF** (pas « ID fiscal »),
 * RC, NIS, article, et adresse avec légende **Adresse :** sur une seule ligne.
 * Exporté pour réutilisation côté DocumentCreation (téléchargement) sans dupliquer la logique.
 */
export function normalizeClientDataForInvoicePreview(raw) {
  if (raw == null || typeof raw !== 'object') return {};
  const c = { ...raw };
  const phoneMerged = (c.phone || c.phone1 || c.mobile || c.mobilePhone || '')
    .toString()
    .trim();
  const emailMerged = (c.email || '').toString().trim();
  const contactMerged = (c.contact || c.contact_person || '').toString().trim();

  const existingAddr = (c.address || '').toString().trim();
  if (existingAddr.startsWith('Adresse :')) {
    const nif = (c.nif || c.fiscalId || c.fiscal_id || c.ai || c.taxNumber || c.tax_number || '')
      .toString()
      .trim();
    const rc = (c.rc || c.registrationNumber || c.rc_number || c.registration_number || '')
      .toString()
      .trim();
    const nis = (c.nis || c.nis_number || c.NIS || '')
      .toString()
      .trim();
    const article = (c.article || c.art || c.articleNumber || c.article_number || '')
      .toString()
      .trim();
    const name = (c.name || c.company_name || c.company || '')
      .toString()
      .trim();
    const cleanedAddr = existingAddr.replace(/^Adresse\s*:\s*/i, '').trim();
    return {
      ...c,
      name: name || c.name,
      nif,
      rc,
      nis,
      article,
      address: cleanedAddr,
      phone: phoneMerged,
      email: emailMerged,
      contact: contactMerged,
    };
  }
  const nif = (c.nif || c.fiscalId || c.fiscal_id || c.ai || c.taxNumber || c.tax_number || '')
    .toString()
    .trim();
  const rc = (c.rc || c.registrationNumber || c.rc_number || c.registration_number || '')
    .toString()
    .trim();
  const nis = (c.nis || c.nis_number || c.NIS || '')
    .toString()
    .trim();
  const article = (c.article || c.art || c.articleNumber || c.article_number || '')
    .toString()
    .trim();
  const name = (c.name || c.company_name || c.company || '')
    .toString()
    .trim();
  const street = (c.address || c.street || c.adresse || '')
    .toString()
    .trim();
  const city = (c.city || c.ville || '')
    .toString()
    .trim();
  const country = (c.country || '')
    .toString()
    .trim();
  const parts = [];
  if (street) parts.push(street);
  if (city) parts.push(city);
  if (country) parts.push(country);
  else if (street || city) parts.push(getBrandDefaultCountry());
  const addrFlat = parts.join(', ');

  return {
    ...c,
    name: name || c.name,
    nif,
    rc,
    nis,
    article,
    address: addrFlat || street || (c.address ? String(c.address).trim() : ''),
    phone: phoneMerged,
    email: emailMerged,
    contact: contactMerged,
  };
}

/**
 * Sous-total HT from lignes: identical logic to DocumentCreation "Récapitulatif" (no import of DocumentCreation — avoids circular deps).
 */
function sumItemsSubtotalForPreview(formData, settings, clients) {
  const items = formData?.items || [];
  return items.reduce((sum, item) => {
    const lineFromTotal = parseFloat(item.total);
    if (!Number.isNaN(lineFromTotal) && lineFromTotal > 0) {
      return sum + lineFromTotal;
    }
    let pr = 0;
    const clientOrSupplierId = formData.client || formData.supplier;
    const sc = (clients || []).find((c) => c.id?.toString() === String(clientOrSupplierId));
    if (sc && sc.type) {
      pr = getClientTypePreferentialRate(sc.type, settings);
    }
    const bp = parseFloat(item.price || item.unitPrice) || 0;
    const unitPrice = bp * (1 - pr / 100);
    const quantity = parseFloat(item.quantity) || 1;
    return sum + unitPrice * quantity;
  }, 0);
}

/**
 * Full Algerian ladder: must stay in sync with DocumentCreation Calculs & Remises / Récapitulatif.
 * Bon de réception : pas de TVA/TTC/Net à payer — valeur HT indicative des lignes reçues seulement.
 */
function buildTotalsRecapRows(formData, settings, documentType, clients) {
  const f = formData || {};
  const dt = String(documentType ?? '')
    .trim()
    .replace(/_/g, '-')
    .toLowerCase();

  if (dt === 'reception-note' || dt === 'receipt-note') {
    const valeurHT = sumReceptionIndicativeHT(f);
    if (!valeurHT || valeurHT <= 0) return [];
    const fmt = (n) => formatBrandMoney(n);
    const tl = getTotalsRowLabels();
    return [
      {
        id: 'valeur_ht',
        label: tl.receptionIndicative,
        text: fmt(valeurHT),
        isFinal: true,
      },
    ];
  }

  const fromItems = sumItemsSubtotalForPreview(f, settings, clients);
  let subtotal = fromItems;
  if (documentType === 'quote-request' && f.specifications?.length > 0) {
    const specSubtotal = f.specifications.reduce((sum, spec) => {
      const budget = parseFloat(spec.budget) || 0;
      const quantity = parseInt(spec.quantity, 10) || 1;
      return sum + budget * quantity;
    }, 0);
    subtotal = specSubtotal > 0 ? specSubtotal : fromItems;
  }

  let currentTotal = subtotal;
  const remiseRate = f.showRemise ? parseFloat(f.remise) || 0 : 0;
  const remiseAmount = currentTotal * (remiseRate / 100);
  currentTotal -= remiseAmount;
  const netCommercial = currentTotal;
  const transport = f.showTransport ? parseFloat(f.transport) || 0 : 0;
  currentTotal += transport;
  const vatMode = f.vatMode || getFacturationVatMode(settings);
  const tvaRate = f.showTva ? parseFloat(f.tvaRate) || getBrandVatRate() : 0;
  const { totalHT, tvaAmount, totalTTC: ttcBeforeTap } = f.showTva
    ? computeVatBreakdown(currentTotal, tvaRate, vatMode)
    : { totalHT: currentTotal, tvaAmount: 0, totalTTC: currentTotal };
  const tapParsed = parseFloat(f.tapRate);
  const saudi = isSaudiBrand();
  const showTap = !saudi && f.showTap;
  const showTimbre = !saudi && f.showTimbre;
  const showEscompte = !saudi && f.showEscompte;
  const showIrg = !saudi && f.showIrg;
  const showIbs = !saudi && f.showIbs;
  const tapRate = showTap && Number.isFinite(tapParsed) ? tapParsed : 0;
  const tapAmount = totalHT * (tapRate / 100);
  currentTotal = ttcBeforeTap + tapAmount;
  const totalTTC = currentTotal;
  const calcTimbre = (base) => {
    if (base <= 300) return 0;
    const tranches = Math.ceil(base / 100);
    let t;
    if (base <= 30000) t = tranches * 1;
    else if (base <= 100000) t = tranches * 1.5;
    else t = tranches * 2;
    return Math.max(5, t);
  };
  const timbre = showTimbre ? calcTimbre(totalTTC) : 0;
  currentTotal += timbre;
  const rabais = f.showRabais ? parseFloat(f.rabais) || 0 : 0;
  currentTotal -= rabais;
  const escompteRate = showEscompte ? parseFloat(f.escompte) || 0 : 0;
  const escompteAmount = totalTTC * (escompteRate / 100);
  currentTotal -= escompteAmount;
  const acompte = f.showAcompte ? parseFloat(f.acompte) || 0 : 0;
  currentTotal -= acompte;
  const avance = f.showAvance ? parseFloat(f.avance) || 0 : 0;
  currentTotal -= avance;
  const garantie = f.showGarantie ? parseFloat(f.garantie) || 0 : 0;
  currentTotal -= garantie;
  const irgRate = showIrg ? parseFloat(f.irg) || 0 : 0;
  const irgAmount = totalTTC * (irgRate / 100);
  currentTotal -= irgAmount;
  const ibsRate = showIbs ? parseFloat(f.ibs) || 0 : 0;
  const ibsAmount = totalTTC * (ibsRate / 100);
  currentTotal -= ibsAmount;
  const retour = f.showRetour ? parseFloat(f.retour) || 0 : 0;
  currentTotal += retour;
  const netAPayer = currentTotal;

  const fmt = (n) => formatBrandMoney(n);
  const neg = (n) => `-${formatBrandMoney(n)}`;
  const tl = getTotalsRowLabels();

  const rows = [];
  rows.push({ id: 'sous_ht', label: tl.subtotal, text: fmt(subtotal) });
  if (f.showRemise) {
    rows.push({ id: 'remise', label: tl.commercialDiscount(remiseRate), text: neg(remiseAmount), isNegative: true });
    rows.push({ id: 'net_com', label: tl.netCommercial, text: fmt(netCommercial) });
  }
  if (f.showTransport) {
    rows.push({ id: 'trans', label: tl.transport, text: fmt(transport) });
  }
  rows.push({ id: 'tht', label: tl.totalExVat, text: fmt(totalHT) });
  if (f.showTva) {
    rows.push({ id: 'tva', label: `${tl.vat} (${tvaRate}%):`, text: fmt(tvaAmount) });
  }
  if (showTap) {
    rows.push({ id: 'tap', label: tl.tap(tapRate), text: fmt(tapAmount) });
  }
  rows.push({ id: 'ttc', label: tl.totalIncVat, text: fmt(totalTTC) });
  if (showTimbre) {
    rows.push({ id: 'timbre', label: tl.fiscalStamp, text: fmt(timbre) });
  }
  if (f.showRabais) {
    rows.push({ id: 'rabais', label: tl.rabais, text: neg(rabais), isNegative: true });
  }
  if (showEscompte) {
    rows.push({ id: 'escompte', label: tl.escompte(escompteRate), text: neg(escompteAmount), isNegative: true });
  }
  if (f.showAcompte) {
    rows.push({ id: 'acompte', label: tl.downPayment, text: neg(acompte), isNegative: true });
  }
  if (f.showAvance) {
    rows.push({ id: 'avance', label: tl.advance, text: neg(avance), isNegative: true });
  }
  if (f.showGarantie) {
    rows.push({ id: 'garantie', label: tl.guaranteeHold, text: neg(garantie), isNegative: true });
  }
  if (showIrg) {
    rows.push({ id: 'irg', label: tl.irgWithholding(irgRate), text: neg(irgAmount), isNegative: true });
  }
  if (showIbs) {
    rows.push({ id: 'ibs', label: tl.ibsWithholding(ibsRate), text: neg(ibsAmount), isNegative: true });
  }
  if (f.showRetour) {
    rows.push({ id: 'retour', label: tl.returnCost, text: fmt(retour) });
  }
  rows.push({ id: 'net', label: tl.netPayable, text: fmt(netAPayer), isFinal: true });
  return rows;
}

// --- Aperçu : Informations principales + cartes spécifiques (ACHATS, etc.) — tout reste dans ce fichier ---

function formatDateFr(iso) {
  return formatPreviewDate(iso);
}

function formatPaymentMethod(pm) {
  return formatPreviewPaymentMethod(pm);
}

function formatOrderOrigin(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.orderOrigin);
}

function formatPaymentTypeInvoice(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.paymentTypeInvoice);
}

function formatPriority(v) {
  return formatPreviewPriority(v);
}

function formatInvoiceType(v) {
  if (!v) return '';
  const vLower = String(v).toLowerCase().replace(/[_\s]/g, '_');
  return PREVIEW_ENUM_VALUES.invoiceType[vLower] || String(v);
}

function formatDeliveryMethod(v) {
  if (!v) return '';
  const vLower = String(v).toLowerCase().replace(/[_\s]/g, '_');
  return PREVIEW_ENUM_VALUES.deliveryMethod[vLower] || String(v);
}

function formatReturnType(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.returnType);
}

export function formatReturnReason(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.returnReason);
}

/** Un ou plusieurs motifs (chaîne « a|b » ou tableau). */
export function formatReturnReasons(value) {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  const parts = Array.isArray(value)
    ? value
    : String(value).includes('|')
      ? String(value).split('|')
      : [value];
  return parts
    .map((p) => formatReturnReason(p))
    .filter(Boolean)
    .join(', ');
}

function formatGoodsCondition(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.goodsCondition);
}

function formatProjectType(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.projectType);
}

function formatPaymentTerms(v) {
  const mapped = mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.paymentTerms);
  const raw = String(v == null ? '' : v).trim();
  if (mapped !== raw) return mapped;
  if (!raw.includes('_')) return raw;
  return raw
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Libellés liste déroulante Devis → PDF */
function formatQuotePaymentTerms(v) {
  if (v == null || v === '') return '';
  const key = String(v).trim().toLowerCase();
  const mapped = PREVIEW_ENUM_VALUES.quotePaymentTerms[key];
  if (mapped) return mapped;
  return formatPaymentTerms(v);
}

function formatReceptionStockDestination(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.receptionStockDestination);
}

function formatReceptionQualityCompliance(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.receptionQualityCompliance);
}

function formatReceptionQuantityCompliance(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.receptionQuantityCompliance);
}

function formatReceptionOverallCondition(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.receptionOverallCondition);
}

function formatReceptionCarrierLabel(v) {
  const mapped = mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.receptionCarrier);
  const raw = String(v == null ? '' : v).trim();
  return mapped !== raw || !raw.includes('_') ? mapped : String(v).trim();
}

function formatStockEntrySource(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.stockEntrySource);
}

function formatStockEntryCondition(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.stockEntryCondition);
}

function formatExitOrTransferDestination(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.exitOrTransferDestination);
}

function formatLogisticsTransportMode(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.logisticsTransportMode);
}

function formatSettlementType(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.settlementType);
}

function formatRefundMethod(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.refundMethod);
}

/**
 * Formulaire DocumentCreation utilise creditNote.{applicationDate,type,approvedBy,refundMethod}
 * et originalInvoice.{number,date,amount} — le PDF / API utilisent les champs plats.
 */
export function coerceCreditNoteFormData(formData) {
  const f = formData || {};
  const cn = f.creditNote || {};
  const inv = f.originalInvoice || {};
  const nestedAmt = inv.amount;
  const nestedAmtDefined =
    nestedAmt != null &&
    nestedAmt !== '' &&
    !(typeof nestedAmt === 'number' && Number.isNaN(nestedAmt));

  let mergedInvoiceAmount = f.originalInvoiceAmount;
  if (nestedAmtDefined) {
    mergedInvoiceAmount = nestedAmt;
  }

  const creditNoteType =
    f.creditNoteType ||
    cn.type ||
    f.creditType ||
    f.credit_type ||
    '';
  const creditNoteRefundMethod =
    f.creditNoteRefundMethod ||
    cn.refundMethod ||
    f.refundMethod ||
    f.refund_method ||
    '';
  const creditNoteApplicationDate =
    f.creditNoteApplicationDate ||
    cn.applicationDate ||
    f.credit_note_application_date ||
    '';

  return {
    ...f,
    creditNoteApplicationDate,
    creditNoteType,
    creditType: creditNoteType,
    creditNoteApprovedBy: f.creditNoteApprovedBy || cn.approvedBy || '',
    creditNoteRefundMethod,
    refundMethod: creditNoteRefundMethod,
    creditNote: {
      ...cn,
      applicationDate: creditNoteApplicationDate || cn.applicationDate || '',
      type: creditNoteType,
      refundMethod: creditNoteRefundMethod,
    },
    originalInvoiceNumber:
      f.originalInvoiceNumber || inv.number || f.original_invoice_number || '',
    originalInvoiceDate: resolveExplicitOriginalInvoiceDate(f),
    originalInvoiceAmount: mergedInvoiceAmount,
  };
}

function formatCreditNoteType(v) {
  return mapPreviewEnumValue(v, PREVIEW_ENUM_VALUES.creditNoteType);
}

function normDocType(documentType) {
  if (documentType == null || documentType === '') return '';
  const t = String(documentType).trim().replace(/_/g, '-').toLowerCase();

  const alias = {
    'credit-note': 'credit-note',
    'creditnote': 'credit-note',
    'proforma-request': 'proforma-request',
    'proformarequest': 'proforma-request',
    'quote-request': 'quote-request',
    quoterequest: 'quote-request',
    'exit-slip': 'exit-note',
    'exit-note': 'exit-note',
    'transfer-slip': 'transfer-note',
    'transfer-note': 'transfer-note',
    'receipt-note': 'reception-note',
    'reception-note': 'reception-note',
    'purchase-order': 'purchase-order',
    purchaseorder: 'purchase-order',
    'return-slip': 'return-slip',
    returnslip: 'return-slip',
    'material-exit': 'material-exit',
    materialexit: 'material-exit',
    'delivery-note': 'delivery-note'
  };

  if (alias[t]) return alias[t];

  /* Legacy explicit checks kept for hyphenated originals */
  if (documentType === 'credit_note') return 'credit-note';
  if (documentType === 'proforma_request') return 'proforma-request';
  if (documentType === 'quote_request') return 'quote-request';
  if (documentType === 'exit_slip') return 'exit-note';
  if (documentType === 'transfer_slip') return 'transfer-note';
  if (documentType === 'receipt_note') return 'reception-note';
  if (documentType === 'purchase_order') return 'purchase-order';
  if (documentType === 'return_slip') return 'return-slip';
  if (documentType === 'material_exit' || documentType === 'material-exit') return 'material-exit';

  /** Normalised hyphen id (underscore + casing variants) pour les derniers fallback. */
  return t;
}

// --- Informations complémentaires + « Bon pour » (aligné DocumentCreation — tout dans ce fichier) ---

export function getSupplementaryValueKey(documentType) {
  const id = normDocType(documentType);
  if (id === 'quote') return 'conditions';
  if (id === 'purchase-order') return 'terms';
  /** Aligné DocumentCreation : la grande zone « Informations complémentaires » du BL est liée à `deliveryNotes` (pas `deliveryConditions` qui sert plutôt au détail franco / modalités courtes). */
  if (id === 'delivery-note' || id === 'delivery' || id === 'material-exit') return 'deliveryNotes';
  if (id === 'shipping-slip' || id === 'shipping') return 'instructions';
  if (id === 'quote-request' || id === 'proforma-request') return 'requirements';
  if (id === 'proforma') return 'conditions';
  if (id === 'attachment') return 'notes';
  return 'notes';
}

function getSupplementaryTextForPreview(formData, documentType) {
  const f = formData || {};
  const key = getSupplementaryValueKey(documentType);
  if (key === 'deliveryNotes') {
    const merged = f.deliveryNotes ?? f.delivery_notes ?? f.notes ?? f.deliveryConditions;
    return merged != null ? String(merged) : '';
  }
  const v = f[key];
  return v != null ? String(v) : '';
}

function isSectionAvailableForSupplementary(section, docId) {
  const dt = docId;
  switch (section) {
    case SUPPLEMENTARY_SECTIONS.notes:
      return true;
    case SUPPLEMENTARY_SECTIONS.conditions:
      return ['quote', 'proforma', 'invoice', 'quote-request', 'proforma-request', 'purchase-order'].includes(dt);
    case SUPPLEMENTARY_SECTIONS.objective:
      return ['quote', 'proforma', 'invoice', 'quote-request', 'proforma-request', 'purchase-order', 'shipping-slip', 'reception-note'].includes(dt);
    case SUPPLEMENTARY_SECTIONS.observations:
      return true;
    case SUPPLEMENTARY_SECTIONS.guarantees:
      return ['quote', 'proforma', 'invoice', 'quote-request', 'proforma-request', 'purchase-order'].includes(dt);
    case SUPPLEMENTARY_SECTIONS.validity:
      return ['quote', 'proforma', 'invoice', 'quote-request', 'proforma-request', 'purchase-order', 'shipping-slip', 'reception-note', 'credit-note'].includes(dt);
    case SUPPLEMENTARY_SECTIONS.motif:
      return true;
    default:
      return false;
  }
}

function isSupplementarySectionActive(formData, section) {
  const showField = getSupplementarySectionShowField(section);
  if (!showField) return false;
  return formData[showField] !== false;
}

function buildDynamicSupplementaryTitle(formData, docId) {
  const f = formData || {};
  const order = normalizeSupplementarySectionOrder(f.sectionOrder);
  const activeSections = [];
  order.forEach((section) => {
    if (!isSectionAvailableForSupplementary(section, docId)) return;
    if (!isSupplementarySectionActive(f, section)) return;
    activeSections.push({ key: section, label: getSupplementarySectionPreviewLabel(section) });
  });
  if (activeSections.length === 0) return '';
  if (activeSections.length === 1) return activeSections[0].label;
  if (activeSections.length === 2) {
    return `${activeSections[0].label} و ${activeSections[1].label}`;
  }
  if (activeSections.length === 3) {
    return `${activeSections[0].label}، ${activeSections[1].label} و ${activeSections[2].label}`;
  }
  const shortNames = activeSections.map((s) => s.label);
  return `${shortNames.slice(0, -1).join('، ')} و ${shortNames[shortNames.length - 1]}`;
}

function buildSupplementaryInfoForPreview(formData, documentType) {
  const docId = normDocType(documentType);
  const text = getSupplementaryTextForPreview(formData, documentType);
  const trimmed = text.trim();
  let label = buildDynamicSupplementaryTitle(formData, docId);
  if (!label && trimmed) {
    label = PREVIEW_LABELS.supplementaryInfo;
  }
  const f = formData || {};
  const order = normalizeSupplementarySectionOrder(f.sectionOrder);
  let hasActiveToggle = false;
  for (const section of order) {
    if (!isSectionAvailableForSupplementary(section, docId)) continue;
    if (isSupplementarySectionActive(f, section)) {
      hasActiveToggle = true;
      break;
    }
  }
  // ✅ Only show if showSupplementarySections is true (default: true)
  const showSupplementarySections = f.showSupplementarySections !== false;
  const show = showSupplementarySections && (Boolean(trimmed) || hasActiveToggle);
  return { show, label, text, valueKey: getSupplementaryValueKey(documentType) };
}

function getBonPourBlockTitleForPreview(docId) {
  if (docId === 'quote-request' || docId === 'proforma-request' || docId === 'proforma_request') {
    return 'موافقة على طلب عرض الأسعار';
  }
  if (docId === 'purchase-order' || docId === 'purchase_order') {
    return 'موافقة على أمر الشراء';
  }
  if (docId === 'return-slip' || docId === 'return_slip') {
    return 'موافقة على إرجاع للمورد';
  }
  if (docId === 'payment-voucher') {
    return 'موافقة على سند الصرف';
  }
  if (docId === 'quote') {
    return 'موافقة على عرض السعر';
  }
  if (docId === 'proforma') {
    return 'موافقة على الفاتورة الأولية';
  }
  if (docId === 'invoice') {
    return 'موافقة على الفاتورة';
  }
  if (docId === 'material-exit' || docId === 'delivery-note' || docId === 'delivery') {
    return 'موافقة على التسليم';
  }
  if (docId === 'payment-receipt') {
    return 'موافقة على سند القبض';
  }
  if (docId === 'client-return-slip') {
    return 'موافقة على إرجاع العميل';
  }
  if (docId === 'credit-note' || docId === 'credit_note') {
    return 'موافقة على الإشعار الدائن';
  }
  if (docId === 'reception-note') {
    return 'موافقة على الاستلام';
  }
  if (docId === 'stock-entry') {
    return 'موافقة على الإدخال للمخزون';
  }
  if (docId === 'exit-note' || docId === 'exit_slip') {
    return 'موافقة على الصرف من المخزون';
  }
  if (docId === 'transfer-note' || docId === 'transfer_slip') {
    return 'موافقة على النقل';
  }
  if (docId === 'shipping-slip' || docId === 'shipping') {
    return 'موافقة على الشحن';
  }
  return 'موافقة على المستند';
}

function buildBonPourInfoForPreview(formData, documentType) {
  const f = formData || {};
  const docId = normDocType(documentType);
  const dateRaw = f.bonPourDate || f.bon_pour_date || '';
  return {
    enabled: f.showBonPour !== false,
    title: getBonPourBlockTitleForPreview(docId),
    name: String(f.bonPourName || f.bon_pour_name || ''),
    role: String(f.bonPourRole || f.bon_pour_role || ''),
    date: dateRaw ? formatDateFr(dateRaw) : '',
    place: String(f.bonPourPlace || f.bon_pour_place || '')
  };
}

function buildEtablieParInfoForPreview(formData) {
  const f = formData || {};
  const dateRaw = f.etablieParDate || '';
  return {
    enabled: f.showEtabliePar === true,
    name: String(f.etablieParName || ''),
    role: String(f.etablieParRole || ''),
    date: dateRaw ? formatDateFr(dateRaw) : '',
    place: String(f.etablieParPlace || '')
  };
}

function buildRecueParInfoForPreview(formData) {
  const f = formData || {};
  const dateRaw = f.recueParDate || '';
  return {
    enabled: f.showRecuePar === true,
    name: String(f.recueParName || ''),
    role: String(f.recueParRole || ''),
    date: dateRaw ? formatDateFr(dateRaw) : '',
    place: String(f.recueParPlace || '')
  };
}

function addDetailRow(rows, label, value) {
  if (value == null) return;
  const v = String(value).trim();
  if (!v) return;
  rows.push({ id: `${label}-${rows.length}`, label: translatePreviewRowLabel(label), value: v });
}

/**
 * Version de addDetailRow qui ajoute TOUJOURS la ligne (avec "---" si vide)
 * Utilisé pour le header où on veut toujours 3 lignes (N° + 2 dates)
 */
function addHeaderRow(rows, label, value) {
  const v = (value == null || String(value).trim() === '') ? '---' : String(value).trim();
  rows.push({ id: `${label}-${rows.length}`, label: translatePreviewRowLabel(label), value: v });
}

function pickQuoteRequestManualBudgetValue(formData) {
  const form = formData || {};
  const keys = [
    'maxBudget',
    'estimatedBudget',
    'approximateBudget',
    'budgetMaximum',
    'budgetMax',
    'budgetEstimate',
    'max_budget',
    'budget_estime',
  ];
  for (const k of keys) {
    const raw = form[k];
    if (raw == null) continue;
    if (typeof raw === 'string' && raw.trim() === '') continue;
    const digits = String(raw).trim().replace(/\s/g, '').replace(/\u00a0/g, '');
    if (digits === '') continue;
    let n = Number(digits.replace(',', '.'));
    if (!Number.isNaN(n) && n > 0) return n;
    n = Number(digits.replace(/\./g, '').replace(',', '.'));
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const nested = form.quoteRequest || form.quote_request;
  if (nested != null && nested.maxBudget != null && nested.maxBudget !== '') {
    const n = Number(String(nested.maxBudget).replace(/\s/g, '').replace(/\u00a0/g, '').replace(',', '.'));
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return null;
}

/** @deprecated alias — budget manuel uniquement (pas prix lignes / amount). */
function pickQuoteRequestBudgetValue(formData) {
  return pickQuoteRequestManualBudgetValue(formData);
}

/**
 * Lignes pour le HEADER (juste sous le titre) : N° document + 2 dates principales
 */
function buildHeaderDetailRows(documentType, formData) {
  const f = formData || {};
  const dt = normDocType(documentType);
  const rows = [];

  addHeaderRow(rows, PREVIEW_LABELS.documentNumber, f.number);
  addHeaderRow(rows, PREVIEW_LABELS.issueDate, formatDateFr(f.date));

  if (dt === 'quote-request' || dt === 'proforma-request') {
    addHeaderRow(rows, PREVIEW_LABELS.responseDeadline, formatDateFr(f.responseDate));
  } else if (dt === 'purchase-order') {
    addHeaderRow(rows, PREVIEW_LABELS.desiredDelivery, formatDateFr(f.deliveryDate));
  } else if (dt === 'return-slip') {
    addHeaderRow(rows, PREVIEW_LABELS.processingDeadline, formatDateFr(f.processingDeadline));
  } else if (dt === 'quote') {
    addHeaderRow(rows, PREVIEW_LABELS.validUntil, formatDateFr(f.validUntil));
  } else if (dt === 'proforma') {
    addHeaderRow(rows, PREVIEW_LABELS.validUntil, formatDateFr(f.validUntil));
  } else if (dt === 'invoice') {
    addHeaderRow(rows, PREVIEW_LABELS.dueDate, formatDateFr(f.dueDate));
  } else if (dt === 'reception-note') {
    addHeaderRow(rows, PREVIEW_LABELS.expectedReception, formatDateFr(f.expectedReceptionDate));
  } else if (dt === 'material-exit' || dt === 'delivery-note' || dt === 'delivery') {
    const sched = f.expectedDeliveryDate || f.deliveryDate;
    addHeaderRow(rows, PREVIEW_LABELS.expectedDelivery, formatDateFr(sched));
  } else if (dt === 'client-return-slip') {
    addHeaderRow(rows, PREVIEW_LABELS.returnDate, formatDateFr(f.clientReturnReturnDate));
  } else if (dt === 'stock-entry') {
    addHeaderRow(rows, PREVIEW_LABELS.expectedEntry, formatDateFr(f.expectedEntryDate));
  } else if (dt === 'exit-note') {
    addHeaderRow(rows, PREVIEW_LABELS.expectedExit, formatDateFr(f.exitNoteExpectedDate || f.date));
  } else if (dt === 'transfer-note') {
    addHeaderRow(rows, PREVIEW_LABELS.expectedTransfer, formatDateFr(f.transferNoteExpectedDate || f.date));
  } else if (dt === 'shipping-slip') {
    addHeaderRow(rows, PREVIEW_LABELS.shipmentDate, formatDateFr(f.shipmentDate || f.date));
  } else if (dt === 'payment-voucher' || dt === 'payment_slip') {
    addHeaderRow(rows, PREVIEW_LABELS.expectedCashing, formatDateFr(f.expectedCashingDate));
  } else if (dt === 'payment-receipt' || dt === 'payment_receipt') {
    addHeaderRow(rows, PREVIEW_LABELS.paymentDate, formatDateFr(f.paymentDate || f.date));
  } else if (dt === 'credit-note') {
    const fc = coerceCreditNoteFormData(f);
    addHeaderRow(rows, PREVIEW_LABELS.applicationDate, formatDateFr(fc.creditNoteApplicationDate));
  } else if (f.dueDate) {
    addHeaderRow(rows, PREVIEW_LABELS.deadline, formatDateFr(f.dueDate));
  }

  return rows;
}

/**
 * Lignes du bloc « DÉTAILS » (droite) : infos du formulaire (établi par, statut, paiement…).
 * N° et dates principales sont maintenant dans le header.
 */
function buildDocumentDetailRows(documentType, formData) {
  const f = formData || {};
  const dt = normDocType(documentType);
  const rows = [];

  if (dt === 'payment-receipt') {
    // Statut Initial is internal - not displayed in PDF
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    const pm = formatPaymentMethod(f.paymentMethod);
    if (pm) addDetailRow(rows, 'Mode de paiement', pm);
  } else if (dt === 'payment-voucher' || dt === 'payment_slip') {
    if (!f.showEtabliePar) {
      addDetailRow(rows, 'Établi par', f.issuedBy);
    }
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Motif du paiement', formatPreviewPaymentVoucherReason(f.paymentReason || f.motif));
    const pm = formatPaymentMethod(f.paymentMethod);
    if (pm) addDetailRow(rows, 'Mode de paiement', pm);
    addDetailRow(rows, 'Transaction / chèque', f.transactionNumber);
    addDetailRow(rows, 'Réf. avoir sur achat', f.supplierCreditNoteRef);
    addDetailRow(rows, 'Réf. facture fournisseur', f.relatedInvoices);
    addDetailRow(rows, 'Banque émetteur', f.issuerBank);
    addDetailRow(rows, 'Compte émetteur', f.issuerAccount);
    addDetailRow(rows, 'Banque réceptrice', f.receiverBank);
    addDetailRow(rows, 'Compte récepteur', f.receiverAccount);
  } else if (dt === 'invoice') {
    if (!f.showEtabliePar) {
      addDetailRow(rows, 'Établi par', f.issuedBy);
    }
    addDetailRow(rows, 'Commercial responsable', f.salesPerson || f.commercial);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    if (f.invoiceType) {
      addDetailRow(rows, 'Type de facture', formatInvoiceType(f.invoiceType));
    }

    addDetailRow(rows, 'Réf. bon de commande', f.orderReference);
    addDetailRow(rows, 'Réf. commande client', f.customerOrderReference);
    addDetailRow(rows, 'Réf. projet/contrat', f.projectReference || f.projectNumber);
    addDetailRow(rows, 'Réf. bon de livraison', f.deliveryReference);
    addDetailRow(rows, 'Lieu de livraison', f.deliveryLocation || f.deliveryAddress);
    if (f.deliveryDate) {
      addDetailRow(rows, 'Date de livraison', formatDateFr(f.deliveryDate));
    }
    const pm = formatPaymentMethod(f.paymentMethod);
    if (pm) addDetailRow(rows, 'Mode de paiement', pm);
  } else if (dt !== 'quote' && dt !== 'proforma' && dt !== 'credit-note' && dt !== 'exit-note' && dt !== 'transfer-note' && dt !== 'reception-note' && dt !== 'stock-entry' && dt !== 'shipping-slip' && dt !== 'delivery-note' && dt !== 'material-exit') {
    const pm = formatPaymentMethod(f.paymentMethod);
    if (pm) addDetailRow(rows, 'Méthode', pm);
  }

  // Hide "Établi par" from PDF if signature section is checked (to avoid redundancy)
  if (
    !f.showEtabliePar &&
    dt !== 'invoice' &&
    dt !== 'reception-note' &&
    dt !== 'payment-voucher' &&
    dt !== 'payment_slip' &&
    dt !== 'client-return-slip' &&
    dt !== 'shipping-slip'
  ) {
    addDetailRow(rows, 'Établi par', f.issuedBy);
  }

  if (dt === 'purchase-order') {
    addDetailRow(rows, 'Acheteur', f.buyerContact);
  }

  if (dt === 'material-exit' || dt === 'delivery-note' || dt === 'delivery') {
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'purchase-order') {
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'quote') {
    // Statut Initial is internal - not displayed in PDF
    addDetailRow(rows, 'Commercial responsable', f.salesPerson);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Type de projet', formatProjectType(f.projectType || f.project_type));
    addDetailRow(rows, 'Réf. projet/contrat', f.projectReference || f.project_reference);
    addDetailRow(
      rows,
      'Délai de réalisation',
      f.deliveryTime || f.estimatedDelivery || f.quoteDeliveryTime,
    );
    addDetailRow(rows, 'Lieu de livraison', f.deliveryLocation || f.delivery_location);
    addDetailRow(rows, 'Délai de livraison', f.deliveryDelay);
    const pmQuote = formatPaymentMethod(f.paymentMethod || f.payment_method);
    if (pmQuote) addDetailRow(rows, 'Méthode de paiement', pmQuote);
    const ptQuote = f.paymentTerms || f.payment_terms;
    if (ptQuote) {
      addDetailRow(rows, 'Conditions de paiement', formatQuotePaymentTerms(ptQuote));
    }
  }

  if (dt === 'proforma') {
    addDetailRow(rows, 'Commercial responsable', f.salesPerson || f.commercial);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Type de projet', formatProjectType(f.projectType || f.project_type));
    addDetailRow(rows, 'Réf. projet/contrat', f.projectReference || f.project_reference);
    addDetailRow(
      rows,
      'Délai de réalisation',
      f.deliveryTime ||
        f.estimatedDelivery ||
        f.estimatedDeliveryTime ||
        f.estimated_delivery_time,
    );
    addDetailRow(rows, 'Lieu de livraison', f.deliveryLocation || f.delivery_location);
    addDetailRow(
      rows,
      'Délai de livraison',
      f.deliveryDelay || f.desiredDeliveryTime || f.desired_delivery_time,
    );
    const pmProforma = formatPaymentMethod(
      f.paymentMethod || f.plannedPaymentMethod || f.payment_method,
    );
    if (pmProforma) addDetailRow(rows, 'Méthode de paiement', pmProforma);
    const ptProforma =
      f.paymentTerms || f.payment_terms || f.desiredPaymentTerms || f.desired_payment_terms;
    if (ptProforma) {
      addDetailRow(rows, 'Conditions de paiement', formatQuotePaymentTerms(ptProforma));
    }
    addDetailRow(rows, 'Date d\'échéance du paiement', formatDateFr(f.dueDate || f.due_date));
  }

  if (dt === 'quote-request' || dt === 'proforma-request') {
    addDetailRow(rows, 'Commercial responsable', f.commercial || f.salesPerson);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Type de projet', formatProjectType(f.projectType || f.project_type));
    addDetailRow(rows, 'Réf. projet/contrat', f.projectReference || f.project_reference);
    addDetailRow(rows, 'Délai de réalisation', f.estimatedDeliveryTime || f.estimated_delivery_time);
    addDetailRow(rows, 'Lieu de livraison', f.deliveryLocation || f.delivery_location || f.deliveryPlace);
    addDetailRow(rows, 'Délai de livraison', f.desiredDeliveryTime || f.desired_delivery_time);
    const budgetQuoteRequest = pickQuoteRequestManualBudgetValue(f);
    if (budgetQuoteRequest != null && budgetQuoteRequest > 0) {
      addDetailRow(
        rows,
        'Budget maximum estimé',
        `${budgetQuoteRequest.toLocaleString(getBrandLocale('ar'))} ${getBrandCurrencySuffix()}`,
      );
    }
    const ptQuoteRequest =
      f.desiredPaymentTerms || f.desired_payment_terms || f.preferredPaymentTerms;
    if (ptQuoteRequest) {
      addDetailRow(rows, 'Conditions de paiement', formatPaymentTerms(ptQuoteRequest));
    }
    addDetailRow(rows, 'Documents requis', f.requiredDocuments || f.required_documents);
  }

  if (dt === 'purchase-order') {
    addDetailRow(rows, 'Réf. projet/contrat', f.projectReference);
    addDetailRow(rows, 'Réf. demande de devis', f.quoteRequestRef);
    addDetailRow(rows, 'Réf. demande de proforma', f.proformaRequestRef);
    addDetailRow(rows, 'Lieu de livraison', f.deliveryAddress || f.deliveryLocation);
    addDetailRow(rows, 'Délai de livraison', f.deliveryTimeframe || f.deliveryDelay);
    addDetailRow(rows, 'Mode de livraison', f.deliveryMode);
    addDetailRow(rows, 'Conditions de paiement', f.paymentTerms);
  }

  if (dt === 'return-slip') {
    const r = f.return || {};
    addDetailRow(
      rows,
      'Contrôlé par',
      r.inspector ||
        f.returnInspector ||
        f.controlledByName ||
        f.controlled_by_name,
    );
    // Statut du retour is internal - not displayed in PDF
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(
      rows,
      'Réf. bon de commande',
      r.originalPurchaseOrderRef ||
        f.returnOriginalPurchaseOrderRef ||
        f.returnOriginalPurchaseOrder,
    );
    addDetailRow(
      rows,
      'Réf. bon de livraison',
      r.originalDeliveryRef || f.returnOriginalDeliveryRef || f.returnOriginalDelivery,
    );
    addDetailRow(
      rows,
      'Réf. facture fournisseur',
      r.originalInvoiceRef || f.returnOriginalInvoiceRef || f.returnOriginalInvoice,
    );
    const brfInvDate =
      r.originalInvoiceDate ||
      f.returnOriginalInvoiceDate ||
      f.return_original_invoice_date ||
      '';
    if (brfInvDate && String(brfInvDate).trim()) {
      addDetailRow(rows, 'Date facture fournisseur', formatDateFr(brfInvDate));
    }
    addDetailRow(rows, "Entrepôt d'origine", r.originWarehouse || f.returnWarehouse);
    addDetailRow(rows, 'Type de retour', formatReturnType(r.type || f.returnType));
    addDetailRow(rows, 'Motif de retour', formatReturnReasons(r.reason || f.returnReason));
    addDetailRow(rows, 'Transporteur retour', r.carrier || f.returnCarrier);
    addDetailRow(rows, 'État des marchandises', formatGoodsCondition(
      r.condition || f.returnCondition || f.goodsCondition || f.goods_condition,
    ));
    addDetailRow(rows, 'Numéro de suivi transporteur', r.trackingNumber || f.returnTrackingNumber);
    addDetailRow(
      rows,
      "Numéro d'autorisation de retour",
      r.rmaNumber || f.returnRMANumber || f.returnRmaNumber,
    );
  }

  if (dt === 'credit-note') {
    const fc = coerceCreditNoteFormData(f);
    if (f.priority) {
      addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    }
    if (fc.creditNoteType) {
      const t = formatCreditNoteType(fc.creditNoteType);
      if (t) addDetailRow(rows, 'Type d\'avoir', t);
    }
  }

  if (dt === 'reception-note') {
    // Ordre : établi/contrôle/priorité → références → lieux stock → reste
    if (!f.showEtabliePar) {
      addDetailRow(rows, 'Établi par', f.issuedBy);
    }
    addDetailRow(rows, 'Contrôlé par', f.receptionControlledBy);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Réf. bon de commande', f.receptionOrderRef);
    addDetailRow(rows, 'Réf. bon de livraison', f.receptionDeliveryNoteRef);
    addDetailRow(rows, 'Entrepôt de réception', f.receptionWarehouse);
    addDetailRow(rows, 'Adresse de réception', f.receiverAddress);
    addDetailRow(rows, 'Destination stock', formatReceptionStockDestination(f.receptionStockDestination));
    addDetailRow(rows, 'Transporteur', formatReceptionCarrierLabel(f.receptionCarrier));
    addDetailRow(rows, 'Heure de réception', f.receptionTime);
    addDetailRow(rows, 'État général', formatReceptionOverallCondition(f.receptionOverallCondition));
    addDetailRow(rows, 'Conformité quantité', formatReceptionQuantityCompliance(f.receptionQuantityCompliance));
    addDetailRow(rows, 'Conformité qualité', formatReceptionQualityCompliance(f.receptionQualityCompliance));
  }

  if (dt === 'stock-entry') {
    // Statut is internal - not displayed in PDF
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'exit-note') {
    // Statut de sortie is internal - not displayed in PDF
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'transfer-note') {
    // Statut du transfert is internal - not displayed in PDF
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'shipping-slip') {
    // Statut is internal - not displayed in PDF
    addDetailRow(rows, 'Établi par', f.issuedBy || f.etablieParName);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
  }

  if (dt === 'client-return-slip') {
    if (!f.showEtabliePar) {
      addDetailRow(rows, 'Établi par', f.issuedBy);
    }
    addDetailRow(rows, 'Contrôlé par', f.clientReturnControlledBy);
    addDetailRow(rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(rows, 'Réf. bon de commande', f.clientReturnOriginalPurchaseOrderRef);
    addDetailRow(rows, 'Réf. facture', f.clientReturnOriginalInvoiceRef);
    addDetailRow(rows, 'Réf. bon de livraison', f.clientReturnOriginalDeliveryRef);
    addDetailRow(
      rows,
      'État des marchandises',
      formatGoodsCondition(
        f.clientReturnCondition || f.returnCondition || f.goodsCondition || f.goods_condition,
      ),
    );
    addDetailRow(rows, 'Type de retour', formatReturnType(f.clientReturnType));
    addDetailRow(
      rows,
      'Motif de retour',
      formatReturnReason(f.clientReturnMotif || f.clientReturnReason),
    );
    addDetailRow(rows, 'Entrepôt de destination', f.clientReturnWarehouse);
    addDetailRow(rows, 'Transporteur retour', f.clientReturnCarrier);
    addDetailRow(
      rows,
      'Numéro de suivi transporteur',
      f.clientReturnTrackingNumber || f.returnTrackingNumber || f.trackingNumber,
    );
    return rows;
  }

  return rows;
}

/**
 * Cartes (ACHATS, VENTES) : mêmes intitulés que les cartes DocumentCreation.
 */
function buildDocumentInfoSections(documentType, formData) {
  const f = formData || {};
  const dt = normDocType(documentType);
  const sections = [];
  const st = translatePreviewSectionTitle;

  if (dt === 'quote') {
    const s1 = { id: 'info-com', title: st('Informations commerciales'), rows: [] };
    addDetailRow(s1.rows, 'Commercial responsable', f.salesPerson);
    addDetailRow(s1.rows, 'Type de projet', formatProjectType(f.projectType || f.project_type));
    addDetailRow(s1.rows, 'Réf. projet/contrat', f.projectReference || f.project_reference);
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'conditions-delais', title: st('Projet & délais'), rows: [] };
    addDetailRow(s2.rows, 'Délai de réalisation', f.deliveryTime || f.estimatedDelivery || f.quoteDeliveryTime);
    addDetailRow(s2.rows, "Lieu d'exécution/livraison", f.executionLocation);
    addDetailRow(s2.rows, "Modalités d'acompte", f.downPaymentTerms);
    if (s2.rows.length) sections.push(s2);

    const s3 = { id: 'livraison-paiement', title: st('Livraison & Paiement'), rows: [] };
    addDetailRow(s3.rows, 'Lieu de livraison', f.deliveryLocation || f.delivery_location);
    addDetailRow(s3.rows, 'Délai de livraison', f.deliveryDelay);
    const pmQuoteSection = formatPaymentMethod(f.paymentMethod || f.payment_method);
    if (pmQuoteSection) addDetailRow(s3.rows, 'Méthode de paiement', pmQuoteSection);
    const ptQuoteSection = f.paymentTerms || f.payment_terms;
    if (ptQuoteSection) {
      addDetailRow(s3.rows, 'Conditions de paiement', formatQuotePaymentTerms(ptQuoteSection));
    }
    if (s3.rows.length) sections.push(s3);
  } else if (dt === 'proforma') {
    const s1 = { id: 'info-com', title: st('Informations commerciales'), rows: [] };
    addDetailRow(s1.rows, 'Commercial responsable', f.commercial || f.salesPerson);
    // Statut Initial is internal - not displayed in PDF
    addDetailRow(s1.rows, 'Priorité', formatPriority(f.priority));
    addDetailRow(s1.rows, 'Type de projet', formatProjectType(f.projectType || f.project_type));
    addDetailRow(s1.rows, 'Réf. projet/contrat', f.projectReference || f.project_reference);
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'livraison-paiement', title: st('Livraison & Paiement'), rows: [] };
    addDetailRow(s2.rows, 'Lieu de livraison', f.deliveryLocation || f.delivery_location);
    addDetailRow(
      s2.rows,
      'Délai de réalisation',
      f.deliveryTime || f.estimatedDelivery || f.estimatedDeliveryTime || f.estimated_delivery_time,
    );
    addDetailRow(
      s2.rows,
      'Délai de livraison',
      f.deliveryDelay || f.desiredDeliveryTime || f.desired_delivery_time,
    );
    addDetailRow(s2.rows, 'Date d\'échéance du paiement', formatDateFr(f.dueDate || f.due_date));
    const pmProforma = formatPaymentMethod(
      f.paymentMethod || f.plannedPaymentMethod || f.payment_method,
    );
    if (pmProforma) addDetailRow(s2.rows, 'Méthode de paiement', pmProforma);
    const ptProforma =
      f.paymentTerms || f.payment_terms || f.desiredPaymentTerms || f.desired_payment_terms;
    if (ptProforma) {
      addDetailRow(s2.rows, 'Conditions de paiement', formatQuotePaymentTerms(ptProforma));
    }
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'invoice') {
    const s2 = { id: 'ref-trace', title: st('Références et traçabilité'), rows: [] };
    addDetailRow(s2.rows, 'Origine de la commande', formatOrderOrigin(f.orderOrigin) || f.orderOrigin);
    if (s2.rows.length) sections.push(s2);

    const s4 = { id: 'modalites-fin', title: st('Modalités financières'), rows: [] };
    const ptForInvoice = f.paymentConditions || f.payment_conditions;
    if (ptForInvoice) {
      addDetailRow(s4.rows, 'Conditions de paiement', formatPaymentTerms(ptForInvoice));
    }
    addDetailRow(s4.rows, 'Type de paiement', formatPaymentTypeInvoice(f.paymentType));
    addDetailRow(s4.rows, "Conditions d'escompte", f.discountConditions);
    addDetailRow(s4.rows, 'Clause de réserve de propriété', f.retentionClause);
    if (s4.rows.length) sections.push(s4);
  } else if (dt === 'material-exit' || dt === 'delivery-note' || dt === 'delivery') {
    const s2 = { id: 'references', title: st('Références'), rows: [] };
    addDetailRow(
      s2.rows,
      'Réf. devis',
      f.quoteReference ||
        f.quote_reference ||
        f.quote_number ||
        f.quoteReferenceNumber
    );
    addDetailRow(
      s2.rows,
      'Réf. facture',
      f.invoiceReference ||
        f.invoice_reference ||
        f.invoice_number ||
        f.relatedInvoice ||
        f.source_invoice_number
    );
    addDetailRow(
      s2.rows,
      'Réf. bon de commande',
      f.orderReference ||
        f.order_reference ||
        f.purchase_order_reference ||
        f.purchaseOrderRef ||
        f.purchase_order_ref
    );
    addDetailRow(
      s2.rows,
      'Réf. commande client',
      f.customerOrderReference ||
        f.customer_order_reference ||
        f.documentReference ||
        f.document_reference
    );
    if (s2.rows.length) sections.push(s2);

    const s3 = { id: 'origine-destination', title: st('Origine & Destination'), rows: [] };
    addDetailRow(s3.rows, 'Entrepôt de départ', f.departureWarehouse || f.departure_warehouse);
    addDetailRow(
      s3.rows,
      'Lieu de livraison',
      f.deliveryAddress ||
        f.delivery_address ||
        f.deliveryLocation ||
        f.delivery_location ||
        f.deliveryPlace
    );
    if (s3.rows.length) sections.push(s3);

    const s4 = { id: 'expedition', title: st('Expédition'), rows: [] };
    addDetailRow(s4.rows, 'Mode de livraison', formatDeliveryMethod(f.deliveryMethod || f.delivery_method));
    addDetailRow(s4.rows, 'Transporteur', f.transporterName || f.carrier || f.transporter_name);
    addDetailRow(
      s4.rows,
      'Numéro de suivi transporteur',
      f.carrierTrackingNumber || f.shipmentCarrierTrackingNumber,
    );
    addDetailRow(s4.rows, 'N° de suivi', f.trackingNumber || f.tracking_number);
    if (f.shipmentPackageCount != null && f.shipmentPackageCount !== '' && !Number.isNaN(Number(f.shipmentPackageCount))) {
      addDetailRow(s4.rows, 'Nombre de colis', String(f.shipmentPackageCount));
    }
    if (s4.rows.length) sections.push(s4);
  } else if (dt === 'payment-receipt') {
    const s1 = { id: 'details-paiement', title: st('Détails du paiement'), rows: [] };
    addDetailRow(s1.rows, 'Référence de paiement', f.paymentReference);
    addDetailRow(s1.rows, 'Type de règlement', formatSettlementType(f.paymentSettlementType));
    addDetailRow(s1.rows, 'Banque émettrice', f.paymentBank);
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'references-factures', title: st('Références factures'), rows: [] };
    if (f.paymentOriginalAmount != null && f.paymentOriginalAmount !== '') {
      const n = Number(f.paymentOriginalAmount);
      if (!Number.isNaN(n) && n > 0) {
        addDetailRow(s2.rows, 'Montant facturé initial', formatBrandMoney(n));
      }
    }
    if (f.paymentRemainingBalance != null && f.paymentRemainingBalance !== '') {
      const n = Number(f.paymentRemainingBalance);
      if (!Number.isNaN(n) && n > 0) {
        addDetailRow(s2.rows, 'Solde restant', formatBrandMoney(n));
      }
    }
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'client-return-slip') {
    // Détails regroupés dans buildDocumentDetailRows (ordre unique)
  } else if (dt === 'credit-note') {
    const fc = coerceCreditNoteFormData(f);
    const cnIsSupplier = !!(
      fc.isSupplier ||
      fc.is_supplier ||
      fc.creditNoteParty === 'supplier' ||
      fc.credit_note_party === 'supplier'
    );
    const refInvoicePdfLabel = cnIsSupplier ? 'Réf. facture fournisseur' : 'Réf. facture';
    const dateInvoicePdfLabel = cnIsSupplier ? 'Date facture fournisseur' : 'Date facture';
    const amountInvoicePdfLabel = cnIsSupplier ? 'Montant facture fournisseur' : 'Montant facture';
    const s1 = {
      id: 'facture-origine',
      title: cnIsSupplier ? 'Facture fournisseur' : 'Réf. facture',
      rows: [],
    };
    addDetailRow(s1.rows, refInvoicePdfLabel, fc.originalInvoiceNumber);
    addDetailRow(s1.rows, dateInvoicePdfLabel, formatDateFr(fc.originalInvoiceDate));
    if (fc.originalInvoiceAmount != null && fc.originalInvoiceAmount !== '') {
      const n = Number(fc.originalInvoiceAmount);
      if (!Number.isNaN(n) && n > 0) {
        addDetailRow(s1.rows, amountInvoicePdfLabel, formatBrandMoney(n));
      }
    }
    const refundFmt = formatRefundMethod(fc.creditNoteRefundMethod);
    if (refundFmt) addDetailRow(s1.rows, 'Mode de remboursement', refundFmt);
    if (s1.rows.length) sections.push(s1);
  } else if (dt === 'proforma-request') {
    const s = { id: 'conditions-demande', title: st('Conditions de la demande'), rows: [] };
    addDetailRow(s.rows, 'Paiement souhaité', f.desiredPaymentTerms);
    addDetailRow(s.rows, 'Délai de validité', f.desiredValidityPeriod);
    addDetailRow(s.rows, 'Documents requis', f.requiredDocuments);
    addDetailRow(s.rows, 'Modalités de livraison', f.deliveryTerms);
    if (s.rows.length) sections.push(s);
  } else if (dt === 'purchase-order') {
    const s = { id: 'bon-commande', title: st('Bon de commande'), rows: [] };
    addDetailRow(s.rows, 'Lieu de livraison', f.deliveryAddress);
    addDetailRow(s.rows, 'Conditions de paiement', f.paymentTerms);
    addDetailRow(s.rows, 'Mode de livraison', f.deliveryMode);
    addDetailRow(s.rows, 'Priorité', f.priority);
    if (s.rows.length) sections.push(s);
  } else if (dt === 'return-slip') {
    const r = f.return || {};
    const s1 = { id: 'retour-info', title: st('Informations du retour'), rows: [] };
    addDetailRow(s1.rows, "Entrepôt d'origine", r.originWarehouse || f.returnWarehouse);
    addDetailRow(s1.rows, 'Motif de retour', formatReturnReasons(r.reason || f.returnReason));
    addDetailRow(s1.rows, 'Type de retour', formatReturnType(r.type || f.returnType));
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'controle', title: st('Contrôle et traitement'), rows: [] };
    addDetailRow(s2.rows, 'État des marchandises', formatGoodsCondition(
      r.condition || f.returnCondition || f.goodsCondition || f.goods_condition,
    ));
    addDetailRow(
      s2.rows,
      'Contrôlé par',
      r.inspector ||
        f.returnInspector ||
        f.controlledByName ||
        f.controlled_by_name,
    );
    // Statut du retour is internal - not displayed in PDF
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'reception-note') {
    // Détails regroupés dans buildDocumentDetailRows (ordre logique unique)
  } else if (dt === 'exit-note') {
    const s1 = { id: 'references-sortie', title: st('Références'), rows: [] };
    addDetailRow(s1.rows, 'Bon de commande client', f.exitNoteClientOrderRef);
    addDetailRow(s1.rows, 'Facture', f.exitNoteInvoiceRef);
    addDetailRow(s1.rows, 'Bon de livraison', f.exitNoteDeliveryNoteRef);
    addDetailRow(s1.rows, 'Bon de transfert', f.exitNoteTransferNoteRef);
    addDetailRow(s1.rows, 'Référence document', f.exitNoteDocumentReference);
    if (s1.rows.length) sections.push(s1);

    const s1b = { id: 'info-sortie', title: st('Informations de sortie'), rows: [] };
    addDetailRow(s1b.rows, 'Destination', formatExitOrTransferDestination(f.exitNoteDestination));
    addDetailRow(s1b.rows, "Entrepôt d'origine", f.exitNoteOriginWarehouse);
    addDetailRow(s1b.rows, 'Entrepôt de destination', f.exitNoteDestinationWarehouse);
    addDetailRow(s1b.rows, 'Mode de transport', formatLogisticsTransportMode(f.exitNoteTransportMode));
    if (s1b.rows.length) sections.push(s1b);

    const s2 = { id: 'autorisation-controle', title: st('Autorisation et contrôle'), rows: [] };
    addDetailRow(s2.rows, 'Approuvé par', f.exitNoteAuthorizedBy);
    addDetailRow(s2.rows, 'Responsable de la sortie', f.exitNoteHandler);
    addDetailRow(s2.rows, 'Heure de sortie', f.exitNoteExitTime);
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'transfer-note') {
    const s1 = { id: 'info-transfert', title: st('Informations de transfert'), rows: [] };
    addDetailRow(s1.rows, "Entrepôt d'origine", f.transferNoteOriginWarehouse);
    addDetailRow(s1.rows, 'Entrepôt de destination', f.transferNoteDestinationWarehouse);
    addDetailRow(s1.rows, 'Référence document', f.transferNoteDocumentReference);
    addDetailRow(s1.rows, 'Demande de transfert', f.transferNoteTransferRequestRef);
    // Statut du transfert is internal - not displayed in PDF
    addDetailRow(s1.rows, 'Mode de transport', formatLogisticsTransportMode(f.transferNoteTransportMode));
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'autorisation-suivi', title: st('Autorisation et suivi'), rows: [] };
    addDetailRow(s2.rows, 'Demandé par', f.transferNoteRequestedBy);
    addDetailRow(s2.rows, 'Approuvé par', f.transferNoteApprovedBy);
    addDetailRow(s2.rows, 'Responsable du transfert', f.transferNoteHandler);
    addDetailRow(s2.rows, 'Heure de départ prévue', f.transferNoteDepartureTime);
    addDetailRow(s2.rows, "Heure d'arrivée prévue", f.transferNoteArrivalTime);
    addDetailRow(s2.rows, 'Numéro de suivi', f.transferNoteTrackingNumber);
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'stock-entry') {
    const s1 = { id: 'info-entree', title: st("Informations d'entrée"), rows: [] };
    addDetailRow(s1.rows, 'Entrepôt de destination', f.stockEntryWarehouse);
    addDetailRow(s1.rows, "Source de l'entrée", formatStockEntrySource(f.stockEntrySource));
    addDetailRow(s1.rows, 'Bon de commande fournisseur', f.stockEntryPurchaseOrderRef);
    addDetailRow(s1.rows, 'Bon de réception', f.stockEntryReceptionNoteRef);
    addDetailRow(s1.rows, 'Bon de transfert', f.stockEntryTransferNoteRef);
    addDetailRow(s1.rows, 'Référence document', f.stockEntryDocumentReference);
    if (s1.rows.length) sections.push(s1);

    const s2 = { id: 'reception-controle', title: st('Réception et contrôle'), rows: [] };
    addDetailRow(s2.rows, 'Reçu par', f.stockEntryReceivedBy);
    addDetailRow(s2.rows, 'Contrôlé par', f.stockEntryControlledBy);
    addDetailRow(s2.rows, 'État de la marchandise', formatStockEntryCondition(f.stockEntryCondition));
    if (s2.rows.length) sections.push(s2);
  } else if (dt === 'shipping-slip') {
    const s0 = { id: 'references-envoi', title: st('Références'), rows: [] };
    addDetailRow(s0.rows, 'Réf. bon de commande client', f.shipmentClientOrderRef || f.shipment?.clientOrderRef);
    addDetailRow(s0.rows, 'Réf. facture', f.shipmentInvoiceRef || f.shipment?.invoiceRef);
    addDetailRow(s0.rows, 'Réf. bon de livraison', f.shipmentDeliveryNoteRef || f.shipment?.deliveryNoteRef);
    if (s0.rows.length) sections.push(s0);

    const s1 = { id: 'info-expedition', title: st("Informations d'expédition"), rows: [] };
    addDetailRow(s1.rows, 'Entrepôt de départ', f.shipmentOriginWarehouse || f.shipment?.originWarehouse);
    addDetailRow(
      s1.rows,
      'Lieu de livraison',
      f.deliveryLocation || f.delivery_location || f.deliveryAddress || f.delivery_address,
    );
    addDetailRow(
      s1.rows,
      'Mode de livraison',
      formatDeliveryMethod(f.shipmentMethod || f.deliveryMethod || f.delivery_method),
    );
    addDetailRow(s1.rows, 'Transporteur', f.shipmentCarrier);
    addDetailRow(s1.rows, 'Numéro de suivi transporteur', f.shipmentCarrierTrackingNumber || f.carrierTrackingNumber);
    addDetailRow(
      s1.rows,
      'N° de suivi',
      f.shipmentTrackingNumber || f.trackingNumber || f.tracking_number,
    );
    // PDF columns: CLIENT | DÉTAILS DOCUMENT | DÉTAILS DU COLIS
    // Priority already displayed in buildDocumentDetailRows
    if (s1.rows.length) sections.push(s1);
  }

  return sections.map((sec) => ({
    ...sec,
    title: translatePreviewSectionTitle(sec.title),
  }));
}

/**
 * Les composants InvoicePreview affichent uniquement `documentDetailRows` dans le bloc « Détails ».
 * Les sections détaillées (`buildDocumentInfoSections`) étaient calculées mais jamais rendues.
 * On concatène ces lignes après les lignes de base en évitant les doublons (même libellé).
 */
function mergedDocumentDetailRowsForPreview(documentType, formData, infoSections) {
  const base = buildDocumentDetailRows(documentType, formData) || [];
  const sections = infoSections || [];
  const seenLabels = new Set(base.map((r) => r.label));
  const appended = [];
  let fallbackId = 0;
  for (const sec of sections) {
    for (const row of sec.rows || []) {
      if (!row?.label || seenLabels.has(row.label)) continue;
      seenLabels.add(row.label);
      appended.push({
        ...row,
        id: row.id || `merged-${sec.id}-${fallbackId++}`,
      });
    }
  }
  return [...base, ...appended];
}

/**
 * Hauteur d’une page en pixels pour A4 (297mm) ou A5 (210mm).
 * Doit correspondre à `paperDimensions.height` et au décalage `top` de chaque tranche d’aperçu.
 */
const paperPageHeightPxCache = new Map();
function getPaperPageHeightPx(paperSize) {
  const p = String(paperSize || 'A4').toLowerCase();
  const mm = p === 'a5' ? 210 : 297;
  if (typeof document === 'undefined') {
    return (mm * 96) / 25.4;
  }
  if (paperPageHeightPxCache.has(mm)) return paperPageHeightPxCache.get(mm);
  const t = document.createElement('div');
  t.setAttribute('style', `height:${mm}mm;position:absolute;visibility:hidden;left:0;top:0;pointer-events:none;`);
  document.body.appendChild(t);
  const h = t.getBoundingClientRect().height;
  document.body.removeChild(t);
  const result = h > 0 ? h : (mm * 96) / 25.4;
  paperPageHeightPxCache.set(mm, result);
  return result;
}

const TemplateWrapper = ({
  documentType,
  formData,
  clientData,
  partnerType,
  paperSize = 'A4',
  onBack,
  onSave,
  isRealTimePreview = false,
  /** When set (e.g. facturation template preview), overrides context — allows demo bank/CCP lines without persisting */
  settings: settingsFromProps,
  /** Remplace le corps facture (lignes, totaux, signatures) par un tableau export (liste facturiers, clients, etc.) */
  dataListExportContent = null,
  /** Titre document en-tête quand dataListExportContent est défini */
  listExportTitle = null,
  /** List export print: continuation page (compact header). */
  listExportContinuation = false,
  listExportPageIndex = 0,
  listExportPageCount = 1,
  /** Rendu pour fenêtre d'impression PDF (sans padding aperçu) */
  isPrintExport = false,
  /** Page seule dans un export multi-pages (sans mesure cachée ni wrapper lourd) */
  listExportEmbedMode = false,
}) => {
  const { settings: settingsFromContext } = useSettings();
  const settings = settingsFromProps ?? settingsFromContext;
  const { products } = useProducts();
  const { clients, suppliers } = useClients();

  // 🎨 Get the visual template component dynamically based on user's selection
  const selectedTemplateId = settings?.design?.selectedModel || 'heritage';
  const VisualTemplate = getTemplateComponent(selectedTemplateId);

  // Physical page box (+ PDF @page)
  const paperDimensions = useMemo(() => {
    const p = String(paperSize || 'A4').toLowerCase();
    if (p === 'a5') return { width: '148mm', height: '210mm', widthPx: 148, heightPx: 210, pageCssSize: 'A5 portrait' };
    return { width: '210mm', height: '297mm', widthPx: 210, heightPx: 297, pageCssSize: 'A4 portrait' };
  }, [paperSize]);

  const isA5 = String(paperSize || 'A4').toUpperCase() === 'A5';
  /** Match invoice preview A5 typography (~1/√2). */
  const A5_SCALE = 0.707106781;
  const footerFs = (px, minPx = 7) => `${isA5 ? Math.max(minPx, Math.round(Number(px) * A5_SCALE)) : Number(px)}px`;
  const footerSp = (px, minPx = 4) => (isA5 ? Math.max(minPx, Math.round(Number(px) * A5_SCALE)) : Number(px));

  // Normalize items to ensure unit_price and product_name are always present for templates
  const normalizedFormData = useMemo(() => {
    const raw = formData || {};
    const dt = normDocType(documentType);
    
    if (dt === 'payment-voucher' || dt === 'payment_slip') {
      const payerDec = decodePaymentVoucherAccountsPayerContact(
        raw.payerContact || raw.payer_contact,
      );
      const linesFromArchive =
        Array.isArray(raw.paymentLines) && raw.paymentLines.length
          ? raw.paymentLines
          : Array.isArray(raw.payments)
            ? raw.payments
            : [];
      const lines =
        linesFromArchive.length > 0
          ? linesFromArchive
          : [{ description: '', amount: 0 }];
      const derivedAmount =
        lines.length > 0
          ? Math.round(
              lines.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0) * 100,
            ) / 100
          : 0;
      const payAmt = parseFloat(raw.paymentAmount ?? raw.payment_amount);
      const paymentAmount =
        Number.isFinite(payAmt) && payAmt > 0 ? payAmt : derivedAmount;
      const issuerAcct =
        raw.issuerAccount != null && String(raw.issuerAccount).trim() !== ''
          ? raw.issuerAccount
          : payerDec.issuerAccount;
      const receiverAcct =
        raw.receiverAccount != null && String(raw.receiverAccount).trim() !== ''
          ? raw.receiverAccount
          : payerDec.receiverAccount;
      return {
        ...raw,
        documentReference: '',
        document_reference: '',
        quoteRequestRef: '',
        quote_request_ref: '',
        proformaRequestRef: '',
        proforma_request_ref: '',
        paymentLines: lines,
        paymentAmount,
        items: [],
        expectedCashingDate:
          raw.expectedCashingDate || raw.dueDate || raw.due_date || '',
        paymentReason: formatPreviewPaymentVoucherReason(raw.paymentReason || raw.motif) || raw.paymentReason || raw.motif || '',
        transactionNumber:
          raw.transactionNumber || raw.transaction_number || '',
        issuerBank: raw.issuerBank || raw.bankName || raw.bank_name || '',
        receiverBank:
          raw.receiverBank || raw.beneficiaryName || raw.beneficiary_name || '',
        issuerAccount: issuerAcct,
        receiverAccount: receiverAcct,
        relatedInvoices:
          raw.relatedInvoices ||
          raw.invoicesPaid ||
          raw.invoices_paid ||
          '',
        purchaseOrderRef:
          raw.purchaseOrderRef ||
          raw.purchaseOrderReference ||
          raw.purchase_order_reference ||
          '',
        supplierCreditNoteRef:
          raw.supplierCreditNoteRef ||
          raw.originalDocumentReference ||
          raw.original_document_reference ||
          '',
      };
    }

    if (dt === 'payment-receipt' || dt === 'payment_receipt') {
      const lines =
        Array.isArray(raw.paymentLines) && raw.paymentLines.length
          ? raw.paymentLines
          : Array.isArray(raw.payments)
            ? raw.payments
            : [];
      const txn =
        raw.paymentReference ||
        raw.transactionNumber ||
        raw.transaction_number ||
        '';
      const bank = raw.paymentBank || raw.bankName || raw.bank_name || '';
      const invNums =
        raw.paymentInvoiceNumbers ||
        raw.invoicesPaid ||
        raw.invoices_paid ||
        '';
      const settlement =
        raw.paymentSettlementType ||
        raw.paymentType ||
        raw.payment_type ||
        '';
      const receivedBy =
        raw.receiptReceivedBy || raw.receivedBy || raw.received_by || '';
      const payDate = raw.paymentDate || raw.payment_date || '';
      const cnRef =
        raw.clientCreditNoteRef ||
        raw.originalDocumentReference ||
        raw.original_document_reference ||
        '';
      return {
        ...raw,
        paymentLines: lines,
        items: [],
        paymentReference: txn,
        transactionNumber: txn,
        paymentBank: bank,
        bankName: bank,
        paymentInvoiceNumbers: invNums,
        invoicesPaid: invNums,
        paymentSettlementType: settlement,
        paymentType: settlement,
        receiptReceivedBy: receivedBy,
        receivedBy,
        paymentDate: payDate,
        clientCreditNoteRef: cnRef,
        paymentOriginalAmount:
          raw.paymentOriginalAmount ??
          raw.initialInvoiceAmount ??
          raw.initial_invoice_amount,
        paymentRemainingBalance:
          raw.paymentRemainingBalance ??
          raw.remainingBalance ??
          raw.remaining_balance,
      };
    }
    
    const base = {
      ...raw,
      items: (raw.items || []).map((item) => {
        const productName =
          item.product_name || item.name || item.designation || item.description || '';
        const isRequestLine = dt === 'quote-request' || dt === 'proforma-request';
        return {
          ...item,
          product_name: productName,
          description: item.description || item.name || item.designation || '',
          unit_price: isRequestLine
            ? 0
            : item.unit_price || item.price || item.unitPrice || 0,
          price: isRequestLine ? 0 : item.price || item.unitPrice || item.unit_price || 0,
          unitPrice: isRequestLine ? 0 : item.unitPrice || item.price || item.unit_price || 0,
          total: isRequestLine ? 0 : item.total || 0,
          quantity: parseFloat(item.quantity) || 0,
        };
      }),
      // Ensure shipping fields are always present for shipping-slip
      shipmentPackageType: raw.shipmentPackageType || '',
      shipmentProductType: raw.shipmentProductType || '',
      shipmentPackageCount: raw.shipmentPackageCount,
      shipmentTotalWeight: raw.shipmentTotalWeight,
      shipmentVolume: raw.shipmentVolume,
      shipmentDeclaredValue: raw.shipmentDeclaredValue,
    };
    if (dt === 'purchase-order') {
      const ddRef =
        raw.quoteRequestRef ||
        raw.documentReference ||
        raw.document_reference ||
        '';
      const dprRef =
        raw.proformaRequestRef ||
        raw.originalDocumentReference ||
        raw.original_document_reference ||
        '';
      if (ddRef && String(ddRef).trim()) base.quoteRequestRef = String(ddRef).trim();
      if (dprRef && String(dprRef).trim()) base.proformaRequestRef = String(dprRef).trim();

      const timeframe =
        raw.deliveryTimeframe ||
        raw.desiredDeliveryTime ||
        raw.desired_delivery_time ||
        '';
      if (timeframe && String(timeframe).trim()) {
        base.deliveryTimeframe = String(timeframe).trim();
      }

      const mode = raw.deliveryMode || raw.delivery_mode || '';
      if (mode && String(mode).trim()) base.deliveryMode = String(mode).trim();

      const payment =
        raw.paymentTerms ||
        raw.desiredPaymentTerms ||
        raw.desired_payment_terms ||
        raw.payment_terms ||
        '';
      if (payment && String(payment).trim()) base.paymentTerms = String(payment).trim();

      const buyer =
        raw.buyerContact || raw.buyer_contact || raw.buyer || '';
      if (buyer && String(buyer).trim()) base.buyerContact = String(buyer).trim();
    }

    if (dt === 'return-slip') {
      base.returnInspector =
        raw.returnInspector ||
        raw.return_inspector ||
        raw.controlledByName ||
        raw.controlled_by_name ||
        '';
      base.returnOriginalInvoiceDate =
        raw.returnOriginalInvoiceDate ||
        raw.return_original_invoice_date ||
        '';
    }

    if (dt === 'client-return-slip') {
      const por =
        raw.clientReturnOriginalPurchaseOrderRef ||
        raw.returnOriginalPurchaseOrder ||
        raw.return_original_purchase_order ||
        '';
      const dor =
        raw.clientReturnOriginalDeliveryRef ||
        raw.returnOriginalDelivery ||
        raw.return_original_delivery ||
        '';
      const ivr =
        raw.clientReturnOriginalInvoiceRef ||
        raw.returnOriginalInvoice ||
        raw.return_original_invoice ||
        '';
      const wh =
        raw.clientReturnWarehouse || raw.returnWarehouse || raw.return_warehouse || '';
      const car =
        raw.clientReturnCarrier || raw.returnCarrier || raw.return_carrier || raw.carrier || '';
      const track =
        raw.clientReturnTrackingNumber ||
        raw.returnTrackingNumber ||
        raw.return_tracking_number ||
        raw.trackingNumber ||
        raw.tracking_number ||
        '';
      const cond =
        raw.clientReturnCondition ||
        raw.returnCondition ||
        raw.goodsCondition ||
        raw.goods_condition ||
        '';
      const rtype = raw.clientReturnType || raw.returnType || raw.return_type || '';
      const motif =
        raw.clientReturnMotif ||
        raw.clientReturnReason ||
        raw.returnReason ||
        raw.return_reason ||
        raw.motif ||
        '';
      const ctrl =
        raw.clientReturnControlledBy ||
        raw.controlledByName ||
        raw.controlled_by_name ||
        raw.returnInspector ||
        raw.return_inspector ||
        '';
      const rd =
        raw.clientReturnReturnDate || raw.returnDate || raw.return_date || raw.date || '';

      if (por) {
        base.clientReturnOriginalPurchaseOrderRef = por;
        base.returnOriginalPurchaseOrder = por;
      }
      if (dor) {
        base.clientReturnOriginalDeliveryRef = dor;
        base.returnOriginalDelivery = dor;
      }
      if (ivr) {
        base.clientReturnOriginalInvoiceRef = ivr;
        base.returnOriginalInvoice = ivr;
      }
      if (wh) {
        base.clientReturnWarehouse = wh;
        base.returnWarehouse = wh;
      }
      if (car) {
        base.clientReturnCarrier = car;
        base.returnCarrier = car;
      }
      if (track) {
        base.clientReturnTrackingNumber = track;
        base.returnTrackingNumber = track;
        base.trackingNumber = track;
      }
      if (cond) {
        base.clientReturnCondition = cond;
        base.returnCondition = cond;
        base.goodsCondition = cond;
      }
      if (rtype) {
        base.clientReturnType = rtype;
        base.returnType = rtype;
      }
      if (motif) {
        base.clientReturnMotif = motif;
        base.clientReturnReason = motif;
        base.returnReason = motif;
      }
      if (ctrl) {
        base.clientReturnControlledBy = ctrl;
        base.controlledByName = ctrl;
      }
      if (rd) {
        base.clientReturnReturnDate = String(rd).slice(0, 10);
        base.returnDate = base.clientReturnReturnDate;
      }
    }

    if (dt === 'delivery-note' || dt === 'delivery' || dt === 'material-exit') {
      const cor =
        raw.customerOrderReference ||
        raw.customer_order_reference ||
        raw.documentReference ||
        raw.document_reference ||
        '';
      const quoteRef =
        raw.quoteReference ||
        raw.quote_reference ||
        (() => {
          const orig =
            raw.originalDocumentReference || raw.original_document_reference || '';
          if (!orig || String(orig).trim() === String(cor).trim()) return '';
          const fromInvoice =
            raw.invoiceReference || raw.invoice_reference || '';
          if (fromInvoice && !raw.quoteReference && !raw.quote_reference && !cor) {
            return '';
          }
          return orig;
        })();
      const bc =
        raw.orderReference ||
        raw.order_reference ||
        raw.purchaseOrderReference ||
        raw.purchase_order_reference ||
        '';
      const inv =
        raw.invoiceReference || raw.invoice_reference || '';
      const wh =
        raw.departureWarehouse ||
        raw.departure_warehouse ||
        raw.originWarehouseName ||
        raw.origin_warehouse_name ||
        '';
      const addr =
        raw.deliveryAddress ||
        raw.delivery_address ||
        raw.deliveryLocation ||
        raw.delivery_location ||
        raw.deliveryPlace ||
        raw.delivery_place ||
        '';
      const transp =
        raw.transporterName || raw.transporter_name || raw.carrier || '';
      const track = raw.trackingNumber || raw.tracking_number || '';
      const carrierTrack =
        raw.carrierTrackingNumber ||
        raw.carrier_tracking_number ||
        raw.shipmentCarrierTrackingNumber ||
        '';
      const pkgRaw = raw.shipmentPackageCount ?? raw.packageCount ?? raw.package_count;
      const dm = raw.deliveryMethod || raw.deliveryMode || raw.delivery_mode || '';
      const sched =
        raw.deliveryDate ||
        raw.delivery_date ||
        raw.expectedDeliveryDate ||
        raw.expected_delivery_date ||
        '';

      if (quoteRef) {
        base.quoteReference = String(quoteRef).trim();
      }
      if (cor) {
        base.customerOrderReference = String(cor).trim();
        base.documentReference = base.customerOrderReference;
      } else {
        const orig =
          raw.originalDocumentReference || raw.original_document_reference || '';
        const fromInvoice = raw.invoiceReference || raw.invoice_reference || '';
        if (orig && fromInvoice && !raw.quoteReference && !raw.quote_reference) {
          base.customerOrderReference = String(orig).trim();
          base.documentReference = base.customerOrderReference;
        }
      }
      if (bc) {
        base.orderReference = bc;
        base.purchaseOrderReference = bc;
      }
      if (inv) base.invoiceReference = inv;
      if (wh) {
        base.departureWarehouse = wh;
        base.originWarehouseName = wh;
      }
      if (addr) {
        base.deliveryAddress = addr;
        base.deliveryLocation = addr;
        base.deliveryPlace = addr;
      }
      if (transp) {
        base.carrier = transp;
        base.transporterName = transp;
      }
      if (track) base.trackingNumber = track;
      if (carrierTrack) {
        base.carrierTrackingNumber = carrierTrack;
        base.shipmentCarrierTrackingNumber = carrierTrack;
      }
      const pkgNum = Number(pkgRaw);
      if (Number.isFinite(pkgNum) && pkgNum >= 0) {
        base.shipmentPackageCount = pkgNum;
        base.packageCount = pkgNum;
      }
      if (dm) {
        base.deliveryMethod = dm;
        base.deliveryMode = dm;
      }
      if (sched) {
        const iso = String(sched).slice(0, 10);
        base.deliveryDate = iso;
        base.expectedDeliveryDate = iso;
      }
      const dn =
        raw.deliveryNotes ??
        raw.delivery_notes ??
        raw.notes ??
        '';
      if (dn != null && String(dn).trim() !== '') {
        base.deliveryNotes = String(dn).trim();
      }
    }

    if (dt === 'reception-note') {
      base.receptionOrderRef =
        raw.receptionOrderRef ||
        raw.purchaseOrderReference ||
        raw.purchase_order_reference ||
        raw.receptionOrder ||
        raw.reception_order ||
        '';
      base.receptionDeliveryNoteRef =
        raw.receptionDeliveryNoteRef ||
        raw.deliveryNoteReference ||
        raw.delivery_note_reference ||
        raw.receptionDeliveryNote ||
        raw.reception_delivery_note ||
        '';
      base.receiverAddress =
        raw.receiverAddress ||
        raw.receptionAddress ||
        raw.reception_address ||
        '';
      const expRec =
        raw.expectedReceptionDate ||
        raw.expected_reception_date ||
        raw.expectedDeliveryDate ||
        raw.expected_delivery_date ||
        '';
      if (expRec) {
        const iso = String(expRec).slice(0, 10);
        base.expectedReceptionDate = iso;
        base.expectedDeliveryDate = iso;
      }
      base.receptionWarehouse =
        raw.receptionWarehouse || raw.warehouseName || raw.warehouse_name || '';
      base.receptionControlledBy =
        raw.receptionControlledBy ||
        raw.controlledByName ||
        raw.controlled_by_name ||
        '';
      base.receptionTime = raw.receptionTime || raw.reception_time || '';
      base.receptionCarrier = raw.receptionCarrier || raw.carrier || '';
      base.items = (raw.items || []).map((item) => normalizeReceptionLineItem(item));
    }

    if (dt === 'stock-entry') {
      base.stockEntryWarehouse =
        raw.stockEntryWarehouse || raw.warehouseName || raw.warehouse_name || '';
      const entryDt =
        raw.stockEntryEntryDate ||
        raw.entryDate ||
        raw.entry_date ||
        raw.expectedEntryDate ||
        raw.expected_entry_date ||
        '';
      if (entryDt) {
        const iso = String(entryDt).slice(0, 10);
        base.stockEntryEntryDate = iso;
        base.expectedEntryDate = iso;
        base.entryDate = iso;
      }
      base.stockEntryEntryTime = raw.stockEntryEntryTime || raw.entryTime || raw.entry_time || '';
      base.stockEntrySource = raw.stockEntrySource || raw.entrySource || raw.entry_source || '';
      base.stockEntryDocumentReference =
        raw.stockEntryDocumentReference ||
        raw.entryDocumentReference ||
        raw.entry_document_reference ||
        '';
      base.stockEntrySupplier =
        raw.stockEntrySupplier || raw.supplierName || raw.supplier_name || raw.client || '';
      base.stockEntryReceivedBy =
        raw.stockEntryReceivedBy || raw.receivedByName || raw.received_by_name || '';
      base.stockEntryControlledBy =
        raw.stockEntryControlledBy || raw.controlledByName || raw.controlled_by_name || '';
      base.stockEntryCondition =
        raw.stockEntryCondition || raw.goodsCondition || raw.goods_condition || '';
      base.stockEntryPurchaseOrderRef =
        raw.stockEntryPurchaseOrderRef ||
        raw.purchaseOrderReference ||
        raw.purchase_order_reference ||
        '';
      base.stockEntryReceptionNoteRef =
        raw.stockEntryReceptionNoteRef ||
        raw.deliveryNoteReference ||
        raw.delivery_note_reference ||
        '';
      base.stockEntryTransferNoteRef =
        raw.stockEntryTransferNoteRef ||
        raw.originalDocumentReference ||
        raw.original_document_reference ||
        '';
    }

    if (dt === 'exit-note') {
      base.exitNoteClientOrderRef =
        raw.exitNoteClientOrderRef ||
        raw.purchaseOrderReference ||
        raw.purchase_order_reference ||
        '';
      base.exitNoteInvoiceRef =
        raw.exitNoteInvoiceRef || raw.invoiceReference || raw.invoice_reference || '';
      base.exitNoteDeliveryNoteRef =
        raw.exitNoteDeliveryNoteRef ||
        raw.deliveryNoteReference ||
        raw.delivery_note_reference ||
        '';
      base.exitNoteTransferNoteRef =
        raw.exitNoteTransferNoteRef ||
        raw.originalDocumentReference ||
        raw.original_document_reference ||
        '';
      base.exitNoteDocumentReference =
        raw.exitNoteDocumentReference ||
        raw.entryDocumentReference ||
        raw.entry_document_reference ||
        '';
      base.exitNoteOriginWarehouse =
        raw.exitNoteOriginWarehouse ||
        raw.originWarehouseName ||
        raw.origin_warehouse_name ||
        '';
      base.exitNoteDestinationWarehouse =
        raw.exitNoteDestinationWarehouse ||
        raw.destinationWarehouseName ||
        raw.destination_warehouse_name ||
        '';
      base.exitNoteDestination =
        raw.exitNoteDestination || raw.deliveryAddress || raw.delivery_address || '';
      base.exitNoteTransportMode =
        raw.exitNoteTransportMode || raw.deliveryMode || raw.delivery_mode || '';
      base.exitNoteAuthorizedBy =
        raw.exitNoteAuthorizedBy || raw.approvedByName || raw.approved_by_name || '';
      base.exitNoteHandler =
        raw.exitNoteHandler ||
        raw.transferResponsibleName ||
        raw.transfer_responsible_name ||
        '';
      base.exitNoteExitTime =
        raw.exitNoteExitTime || raw.departureTime || raw.departure_time || '';
      base.exitNoteRecipient =
        raw.exitNoteRecipient || raw.receivedByName || raw.received_by_name || '';
      const expExit =
        raw.exitNoteExpectedDate ||
        raw.expectedDeliveryDate ||
        raw.expected_delivery_date ||
        raw.date ||
        '';
      if (expExit) base.exitNoteExpectedDate = String(expExit).slice(0, 10);
    }

    if (dt === 'transfer-note') {
      base.transferNoteOriginWarehouse =
        raw.transferNoteOriginWarehouse ||
        raw.originWarehouseName ||
        raw.origin_warehouse_name ||
        '';
      base.transferNoteDestinationWarehouse =
        raw.transferNoteDestinationWarehouse ||
        raw.destinationWarehouseName ||
        raw.destination_warehouse_name ||
        '';
      base.transferNoteDocumentReference =
        raw.transferNoteDocumentReference ||
        raw.entryDocumentReference ||
        raw.entry_document_reference ||
        '';
      base.transferNoteTransferRequestRef =
        raw.transferNoteTransferRequestRef ||
        raw.originalDocumentReference ||
        raw.original_document_reference ||
        '';
      base.transferNoteRequestedBy =
        raw.transferNoteRequestedBy ||
        raw.transferRequestedByName ||
        raw.transfer_requested_by_name ||
        '';
      base.transferNoteHandler =
        raw.transferNoteHandler ||
        raw.transferResponsibleName ||
        raw.transfer_responsible_name ||
        '';
      base.transferNoteApprovedBy =
        raw.transferNoteApprovedBy || raw.approvedByName || raw.approved_by_name || '';
      base.transferNoteTransportMode =
        raw.transferNoteTransportMode ||
        raw.shipmentMode ||
        raw.shipment_mode ||
        raw.deliveryMode ||
        '';
      base.transferNoteDepartureTime =
        raw.transferNoteDepartureTime || raw.departureTime || raw.departure_time || '';
      base.transferNoteArrivalTime =
        raw.transferNoteArrivalTime || raw.arrivalTime || raw.arrival_time || '';
      base.transferNoteTrackingNumber =
        raw.transferNoteTrackingNumber || raw.trackingNumber || raw.tracking_number || '';
      const expTr =
        raw.transferNoteExpectedDate ||
        raw.expectedDeliveryDate ||
        raw.expected_delivery_date ||
        raw.date ||
        '';
      if (expTr) base.transferNoteExpectedDate = String(expTr).slice(0, 10);
    }

    if (dt === 'shipping-slip') {
      base.shipmentDate = raw.shipmentDate || raw.shipment_date || raw.date || '';
      base.shipmentOriginWarehouse =
        raw.shipmentOriginWarehouse ||
        raw.originWarehouseName ||
        raw.origin_warehouse_name ||
        '';
      base.deliveryLocation =
        raw.deliveryLocation ||
        raw.delivery_location ||
        raw.deliveryAddress ||
        raw.delivery_address ||
        raw.lieu_livraison ||
        '';
      base.deliveryAddress = base.deliveryLocation;
      base.shipmentMethod =
        raw.shipmentMethod ||
        raw.shipmentMode ||
        raw.shipment_mode ||
        raw.deliveryMethod ||
        raw.delivery_method ||
        raw.deliveryMode ||
        raw.delivery_mode ||
        '';
      base.shipmentCarrier = raw.shipmentCarrier || raw.carrier || '';
      base.shipmentCarrierTrackingNumber =
        raw.shipmentCarrierTrackingNumber ||
        raw.carrierTrackingNumber ||
        raw.carrier_tracking_number ||
        '';
      base.shipmentTrackingNumber =
        raw.shipmentTrackingNumber || raw.trackingNumber || raw.tracking_number || '';
      base.shipmentClientOrderRef =
        raw.shipmentClientOrderRef ||
        raw.purchaseOrderReference ||
        raw.purchase_order_reference ||
        '';
      base.shipmentInvoiceRef =
        raw.shipmentInvoiceRef || raw.invoiceReference || raw.invoice_reference || '';
      base.shipmentDeliveryNoteRef =
        raw.shipmentDeliveryNoteRef ||
        raw.deliveryNoteReference ||
        raw.delivery_note_reference ||
        '';
      base.shipmentPriority = raw.shipmentPriority || raw.priority || '';
      const pkgRaw =
        raw.shipmentPackageCount ?? raw.packageCount ?? raw.package_count;
      const pkgNum = Number(pkgRaw);
      if (Number.isFinite(pkgNum) && pkgNum >= 0) {
        base.shipmentPackageCount = pkgNum;
        base.packageCount = pkgNum;
      }
      const wt = Number(raw.shipmentTotalWeight ?? raw.weight ?? raw.totalWeight);
      if (Number.isFinite(wt) && wt >= 0) {
        base.shipmentTotalWeight = wt;
        base.weight = wt;
      }
      const vol = Number(raw.shipmentVolume ?? raw.volume);
      if (Number.isFinite(vol) && vol >= 0) {
        base.shipmentVolume = vol;
        base.volume = vol;
      }
      const dv = Number(raw.shipmentDeclaredValue ?? raw.declaredValue);
      if (Number.isFinite(dv) && dv >= 0) {
        base.shipmentDeclaredValue = dv;
        base.declaredValue = dv;
      }
      base.shipmentPackageType = raw.shipmentPackageType || raw.packageType || raw.package_type || '';
      base.shipmentProductType =
        raw.shipmentProductType || raw.productType || raw.product_type || '';
    }

    if (!isSaudiBrand() && (base.showTap === undefined || base.showTap === false)) {
      const tapAmt = parseFloat(raw.totalTap ?? raw.total_tap);
      if (Number.isFinite(tapAmt) && tapAmt > 0) {
        base.showTap = true;
        if (base.tapRate == null || base.tapRate === '') {
          const ht = parseFloat(raw.totalHt ?? raw.total_ht ?? raw.subtotal);
          if (Number.isFinite(ht) && ht > 0) {
            base.tapRate = Math.round((tapAmt / ht) * 10000) / 100;
          }
        }
      }
    }
    if (base.showTva === undefined) {
      if (raw.showTva === false || raw.show_tva === 0) base.showTva = false;
      else if (raw.show_tva != null) base.showTva = Boolean(raw.show_tva);
      else base.showTva = raw.showTva !== false;
    }

    return dt === 'credit-note' ? coerceCreditNoteFormData(base) : base;
  }, [documentType, formData]);

  // Safe localStorage utility with error handling
  const safeGetFromStorage = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(key);
      if (!saved) return defaultValue;
      
      const parsed = JSON.parse(saved);
      
      // Validate data structure based on key
      if (key.includes('Position')) {
        if (typeof parsed === 'object' && parsed !== null && 
            typeof parsed.x === 'number' && typeof parsed.y === 'number' &&
            parsed.x >= POSITION_MIN && parsed.y >= POSITION_MIN && 
            parsed.x < POSITION_MAX_X && parsed.y < POSITION_MAX_Y) {
          return parsed;
        }
      } else if (key.includes('Size')) {
        if (key.includes('Stamp') && typeof parsed === 'number' && 
            parsed >= STAMP_SIZE_MIN && parsed <= STAMP_SIZE_MAX) {
          return parsed;
        } else if (key.includes('Signature') && typeof parsed === 'object' && parsed !== null &&
                   typeof parsed.width === 'number' && typeof parsed.height === 'number' &&
                   parsed.width >= SIGNATURE_WIDTH_MIN && parsed.width <= SIGNATURE_WIDTH_MAX && 
                   parsed.height >= SIGNATURE_HEIGHT_MIN && parsed.height <= SIGNATURE_HEIGHT_MAX) {
          return parsed;
        }
      }
      
      return defaultValue;
    } catch (error) {
      console.warn(`Failed to load ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  const safeSetToStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to save ${key} to localStorage:`, error);
    }
  };

  // Constants for default values and constraints  
  const DEFAULT_STAMP_POSITION = { x: 250, y: 580 };
  const DEFAULT_SIGNATURE_POSITION = { x: 80, y: 580 };
  const DEFAULT_STAMP_SIZE = 120;
  const DEFAULT_SIGNATURE_SIZE = { width: 180, height: 90 };

  // Size constraints
  const STAMP_SIZE_MIN = 40;
  const STAMP_SIZE_MAX = 200;
  const SIGNATURE_WIDTH_MIN = 60;
  const SIGNATURE_WIDTH_MAX = 300;
  const SIGNATURE_HEIGHT_MIN = 30;
  const SIGNATURE_HEIGHT_MAX = 150;

  // Position constraints
  const POSITION_MIN = 0;
  const POSITION_MAX_X = 2000;
  const POSITION_MAX_Y = 2000;
  const DRAG_MARGIN = 20;

  // Initialize positions and sizes with safe localStorage access
  const [stampPosition, setStampPosition] = useState(() => {
    const saved = safeGetFromStorage('qawaemStampPosition', DEFAULT_STAMP_POSITION);
    if (saved.y > 620 || saved.y < 550 || saved.x > 400) {
      const correctedPosition = DEFAULT_STAMP_POSITION;
      safeSetToStorage('qawaemStampPosition', correctedPosition);
      return correctedPosition;
    }
    return saved;
  });
  
  const [signaturePosition, setSignaturePosition] = useState(() => {
    const saved = safeGetFromStorage('qawaemSignaturePosition', DEFAULT_SIGNATURE_POSITION);
    if (saved.y > 620 || saved.y < 550 || saved.x > 300) {
      const correctedPosition = DEFAULT_SIGNATURE_POSITION;
      safeSetToStorage('qawaemSignaturePosition', correctedPosition);
      return correctedPosition;
    }
    return saved;
  });
  
  const [stampSize, setStampSize] = useState(() => 
    safeGetFromStorage('qawaemStampSize', DEFAULT_STAMP_SIZE)
  );
  
  const [signatureSize, setSignatureSize] = useState(() => 
    safeGetFromStorage('qawaemSignatureSize', DEFAULT_SIGNATURE_SIZE)
  );
  
  const [dragItem, setDragItem] = useState(null);
  const documentRef = useRef(null);

  // Drag and Drop functions for stamp and signature positioning
  const handleDragStart = (e, type) => {
    setDragItem(type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (e.currentTarget.classList.contains('document-container')) {
      e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3)';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    
    if (e.currentTarget.classList.contains('document-container')) {
      e.currentTarget.style.boxShadow = '';
    }
    
    if (!dragItem || !documentRef.current) return;

    const documentPaper = documentRef.current.querySelector('.document-paper');
    if (!documentPaper) return;
    
    const rect = documentPaper.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const maxX = rect.width - DRAG_MARGIN;
    const maxY = rect.height - DRAG_MARGIN;

    if (dragItem === 'stamp') {
      const newX = Math.max(DRAG_MARGIN, Math.min(maxX - stampSize, x - stampSize/2));
      const newY = Math.max(DRAG_MARGIN, Math.min(maxY - stampSize, y - stampSize/2));
      const newPosition = { x: newX, y: newY };
      setStampPosition(newPosition);
      safeSetToStorage('qawaemStampPosition', newPosition);
    } else if (dragItem === 'signature') {
      const newX = Math.max(DRAG_MARGIN, Math.min(maxX - signatureSize.width, x - signatureSize.width/2));
      const newY = Math.max(DRAG_MARGIN, Math.min(maxY - signatureSize.height, y - signatureSize.height/2));
      const newPosition = { x: newX, y: newY };
      setSignaturePosition(newPosition);
      safeSetToStorage('qawaemSignaturePosition', newPosition);
    }

    setDragItem(null);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.classList.contains('document-container')) {
      e.currentTarget.style.boxShadow = '';
    }
  };

  // Size adjustment functions
  const adjustSize = (type, delta) => {
    if (type === 'stamp') {
      const newSize = Math.max(STAMP_SIZE_MIN, Math.min(STAMP_SIZE_MAX, stampSize + delta));
      setStampSize(newSize);
      safeSetToStorage('qawaemStampSize', newSize);
    } else if (type === 'signature') {
      const newWidth = Math.max(SIGNATURE_WIDTH_MIN, Math.min(SIGNATURE_WIDTH_MAX, signatureSize.width + delta));
      const newHeight = Math.max(SIGNATURE_HEIGHT_MIN, Math.min(SIGNATURE_HEIGHT_MAX, signatureSize.height + delta/2));
      const newSize = { width: newWidth, height: newHeight };
      setSignatureSize(newSize);
      safeSetToStorage('qawaemSignatureSize', newSize);
    }
  };

  const clientDataNormalized = useMemo(
    () => normalizeClientDataForInvoicePreview(clientData),
    [clientData]
  );

  const handleDownloadPDF = async () => {
    try {
      await downloadDocumentPDF(documentType, normalizedFormData, clientDataNormalized, settings);
    } catch (error) {
        alert('تعذّر تنزيل ملف PDF');
    }
  };

  const totalsRecapRows = useMemo(
    () => buildTotalsRecapRows(normalizedFormData, settings, documentType, clients),
    [normalizedFormData, settings, documentType, clients]
  );

  const exitNoteData = useMemo(() => {
    const dt = normDocType(documentType);
    if (dt !== 'exit-note') return null;
    const f = normalizedFormData || {};
    const pType = f.exitNotePartnerType || 'client';
    return {
      partnerType: pType,
      motif: f.exitNoteMotif || '',
      responsible: f.exitNoteOtherResponsible || '',
      ref: f.exitNoteOtherRef || '',
      beneficiary: f.exitNoteOtherBeneficiary || '',
      description: f.exitNoteOtherDescription || '',
      dateConstat: f.exitNoteOtherDateConstat || '',
      location: f.exitNoteOtherLocation || '',
      warehouseName: f.exitNoteWarehouse || '',
      warehouseCode: f.exitNoteWarehouseCode || '',
      warehouseManager: f.exitNoteWarehouseManager || '',
      warehouseAddress: f.exitNoteWarehouseAddress || '',
    };
  }, [documentType, normalizedFormData]);

  const shippingSlipData = useMemo(() => {
    const dt = normDocType(documentType);
    if (dt !== 'shipping-slip' && dt !== 'shipping') return null;
    const f = normalizedFormData || {};
    return {
      packageType: f.shipmentPackageType || '',
      productType: f.shipmentProductType || '',
      packageCount: f.shipmentPackageCount ?? '',
      totalWeight: f.shipmentTotalWeight ?? '',
      volume: f.shipmentVolume ?? '',
      declaredValue: f.shipmentDeclaredValue ?? '',
    };
  }, [documentType, normalizedFormData]);

  const documentPartnerLabel = useMemo(() => {
    const dt = normDocType(documentType);
    const f = normalizedFormData || {};
    const base = getDocumentPartnerLabelForType(dt, partnerType, f);
    return buildDocumentPartnerSectionLabel(base, clientDataNormalized, {
      formData: f,
      isSupplier: partnerType === 'supplier',
    });
  }, [partnerType, documentType, normalizedFormData, clientDataNormalized]);

  const documentDetailsLabel = useMemo(() => {
    return getDocumentDetailsLabelForType(documentType);
  }, [documentType]);


  // Build warehouse data objects for transfer-note
  const warehouseOriginData = useMemo(() => {
    const dt = normDocType(documentType);
    if (dt !== 'transfer-note') return null;
    
    const f = normalizedFormData || {};
    
    return {
      name: f.transferNoteOriginWarehouse || '',
      code: f.transferNoteOriginWarehouseCode || '',
      address: f.transferNoteOriginWarehouseAddress || '',
      manager: f.transferNoteOriginWarehouseManager || '',
      departureTime: f.transferNoteDepartureTime || ''
    };
  }, [documentType, normalizedFormData]);

  const warehouseDestinationData = useMemo(() => {
    const dt = normDocType(documentType);
    if (dt !== 'transfer-note') return null;
    
    const f = normalizedFormData || {};
    
    return {
      name: f.transferNoteDestinationWarehouse || '',
      code: f.transferNoteDestinationWarehouseCode || '',
      address: f.transferNoteDestinationWarehouseAddress || '',
      manager: f.transferNoteDestinationWarehouseManager || '',
      arrivalTime: f.transferNoteArrivalTime || ''
    };
  }, [documentType, normalizedFormData]);

  const headerDetailRows = useMemo(
    () => buildHeaderDetailRows(documentType, normalizedFormData),
    [documentType, normalizedFormData]
  );

  const documentInfoSections = useMemo(
    () => buildDocumentInfoSections(documentType, normalizedFormData),
    [documentType, normalizedFormData]
  );

  const documentDetailRows = useMemo(
    () => mergedDocumentDetailRowsForPreview(documentType, normalizedFormData, documentInfoSections),
    [documentType, normalizedFormData, documentInfoSections]
  );

  const supplementaryInfo = useMemo(
    () => buildSupplementaryInfoForPreview(normalizedFormData, documentType),
    [normalizedFormData, documentType]
  );

  const bonPourInfo = useMemo(
    () => buildBonPourInfoForPreview(normalizedFormData, documentType),
    [normalizedFormData, documentType]
  );

  const etablieParInfo = useMemo(
    () => buildEtablieParInfoForPreview(normalizedFormData),
    [normalizedFormData]
  );

  const recueParInfo = useMemo(
    () => buildRecueParInfoForPreview(normalizedFormData),
    [normalizedFormData]
  );

  const docTypeForPrecision = useMemo(() => normDocType(documentType), [documentType]);
  const isPrecisionPaymentLike = useMemo(() => {
    const dt = docTypeForPrecision;
    return (
      dt === 'payment-voucher' ||
      dt === 'payment_slip' ||
      dt === 'payment-receipt' ||
      dt === 'payment_receipt'
    );
  }, [docTypeForPrecision]);

  /** Une page + beaucoup de lignes de paiement : resserrer l'espace entre le bloc notes et les signatures (pas en page « closing » du split). */
  const PAYMENT_NOTES_SIG_COMPACT_MIN_LINES = 10;
  const compactNotesSignatureSpacing = useMemo(() => {
    if (!isPrecisionPaymentLike) return false;
    const n = (normalizedFormData?.paymentLines || []).length;
    return n >= PAYMENT_NOTES_SIG_COMPACT_MIN_LINES;
  }, [isPrecisionPaymentLike, normalizedFormData?.paymentLines]);

  const precisionItemCount = useMemo(
    () => (normalizedFormData?.items || []).length,
    [normalizedFormData]
  );

  // Count payment lines for payment-like documents  
  const precisionPaymentLineCount = useMemo(
    () => (normalizedFormData?.paymentLines || []).length,
    [normalizedFormData]
  );

  // Use payment lines for payment documents, items for others
  const effectiveRowCount = useMemo(() => {
    return isPrecisionPaymentLike ? precisionPaymentLineCount : precisionItemCount;
  }, [isPrecisionPaymentLike, precisionPaymentLineCount, precisionItemCount]);

  /**
   * Lignes tableau pour mesure « document entier » (une page) : de maxR à minR.
   * On ne monte pas au-delà de max(9, n) pour ne pas ajouter de lignes vides à un document tenu sur une seule page.
   * For payment vouchers: uses payment line count instead of item count.
   */
  const invoiceRowCandidates = useMemo(() => {
    const minR = effectiveRowCount === 0 ? 1 : effectiveRowCount;
    const maxR = Math.max(9, effectiveRowCount);
    const out = [];
    for (let r = maxR; r >= minR; r -= 1) out.push(r);
    return out;
  }, [effectiveRowCount]);

  /** Split body/closing : autoriser plus de lignes (y compris vides) sur la 1re page pour combler jusqu’au pied. */
  const INVOICE_BODY_SPLIT_EXTRA_EMPTY_ROWS = 45;
  /** Bon de versement / reçu : premières pages souvent plus « courtes » en lignes réelles — autoriser plus de candidats lignes vides pour remplir jusqu’au MONTANT TOTAL. */
  const PAYMENT_LIKE_BODY_SPLIT_EXTRA_EMPTY_ROWS = 56;

  const bodySplitRowCandidates = useMemo(() => {
    const minR = effectiveRowCount === 0 ? 1 : effectiveRowCount;
    const baseMax = Math.max(10, effectiveRowCount);
    const extraEmpty =
      INVOICE_BODY_SPLIT_EXTRA_EMPTY_ROWS +
      (isPrecisionPaymentLike ? PAYMENT_LIKE_BODY_SPLIT_EXTRA_EMPTY_ROWS : 0);
    const bodyMax = baseMax + extraEmpty;
    const out = [];
    for (let r = bodyMax; r >= minR; r -= 1) out.push(r);
    return out;
  }, [effectiveRowCount, isPrecisionPaymentLike]);

  const invoiceRowCandidateSet = useMemo(() => new Set(invoiceRowCandidates), [invoiceRowCandidates]);

  const precisionAllByRRef = useRef({});
  const precisionBodyByRRef = useRef({});
  const precisionClosingMeasureRef = useRef(null);
  const bindPrecisionAllRowRef = useCallback((R) => (el) => {
    if (el) precisionAllByRRef.current[R] = el;
    else delete precisionAllByRRef.current[R];
  }, []);
  const bindPrecisionBodyRowRef = useCallback((R) => (el) => {
    if (el) precisionBodyByRRef.current[R] = el;
    else delete precisionBodyByRRef.current[R];
  }, []);

  /** Aperçu : repères de fin de page (A4 ou A5) quand le document dépasse une page. */
  const a4PrecisionMeasureWrapRef = useRef(null);
  /** Mesure classique (bons de versement / reçu : pas de padding dynamique sur le tableau articles). */
  const a4PreviewFullRef = useRef(null);
  const a4PreviewBodyRef = useRef(null);
  const a4PreviewClosingRef = useRef(null);
  const [a4Preview, setA4Preview] = useState({
    pageH: 0,
    pageCount: 1,
    splitMode: null,
    invoiceTableTotalRows: 10,
  });

  /** Hauteur utilisable au-dessus du pied noir : 297mm − bande pied (+ 8px marge au-dessus). Réserve abaissée un peu + marge mesure pour autoriser ~1–2 lignes tableau de plus sans split. */
  const A4_FOOTER_BAND_RESERVE_PX = 80;
  const PRECISION_CONTENT_FIT_EXTRA_PX = 36;
  /** Si le document entier tient sur une page mais avec très peu de marge, retirer une ligne vide pour éviter que les signatures touchent le pied (mesure vs rendu réel). */
  const SIGNATURE_SATURATION_MIN_SLACK_PX = 8;
  /**
   * Bon de versement / reçu en split body-closing : marge pour le rendu réel (flex + overflow hidden).
   * Trop haut → trop peu de lignes vides et grand vide au-dessus de MONTANT TOTAL ; trop bas → « Arrêtée » peut être rognée.
   */
  const PAYMENT_LIKE_BODY_SPLIT_EXTRA_SLACK_PX = 40;

  const measureA4Pages = useCallback(() => {
    if (dataListExportContent != null) {
      const fullPageH = getPaperPageHeightPx(paperSize);
      if (!fullPageH || fullPageH <= 0) return;
      setA4Preview((prev) => {
        const next = {
          pageH: fullPageH,
          pageCount: 1,
          splitMode: null,
          invoiceTableTotalRows: 9,
        };
        if (
          prev.pageCount === next.pageCount &&
          prev.splitMode === next.splitMode &&
          Math.abs((prev.pageH || 0) - next.pageH) < 0.5
        ) {
          return prev;
        }
        return next;
      });
      return;
    }
    const fullPageH = getPaperPageHeightPx(paperSize);
    if (!fullPageH || fullPageH <= 0) return;
    const refA4H = getPaperPageHeightPx('A4');
    const pageScale = refA4H > 0 ? fullPageH / refA4H : 1;
    const footerReserve = A4_FOOTER_BAND_RESERVE_PX * pageScale;
    const fitExtra = PRECISION_CONTENT_FIT_EXTRA_PX * pageScale;
    const paymentSplit = PAYMENT_LIKE_BODY_SPLIT_EXTRA_SLACK_PX * pageScale;
    const limit = fullPageH - footerReserve + fitExtra;
    const limitBodySplit = limit - (isPrecisionPaymentLike ? paymentSplit : 0);
    const signatureSlackMin = Math.max(4, SIGNATURE_SATURATION_MIN_SLACK_PX * pageScale);
    const EPS = 0.5;

    const samePreview = (prev, next) =>
      prev.splitMode === next.splitMode &&
      prev.pageCount === next.pageCount &&
      Math.abs((prev.pageH || 0) - next.pageH) < EPS &&
      (prev.invoiceTableTotalRows ?? 9) === (next.invoiceTableTotalRows ?? 9);

    if (invoiceRowCandidates.length > 0) {
      const candidates = invoiceRowCandidates;
      const closingEl = precisionClosingMeasureRef.current;
      const hClosing = closingEl ? closingEl.offsetHeight : Infinity;

      let chosenSingle = null;
      for (const R of candidates) {
        const el = precisionAllByRRef.current[R];
        if (!el) continue;
        if (el.offsetHeight <= limit + EPS) {
          chosenSingle = R;
          break;
        }
      }
      if (chosenSingle != null) {
        const minR = candidates[candidates.length - 1];
        if (chosenSingle > minR) {
          const elChosen = precisionAllByRRef.current[chosenSingle];
          if (elChosen) {
            const slack = limit + EPS - elChosen.offsetHeight;
            // Ne retirer une ligne que si vraiment saturé (< 20px de marge)
            if (slack < signatureSlackMin) {
              chosenSingle -= 1;
            }
          }
        }
        setA4Preview((prev) => {
          const next = {
            pageH: fullPageH,
            pageCount: 1,
            splitMode: null,
            invoiceTableTotalRows: chosenSingle,
          };
          return samePreview(prev, next) ? prev : next;
        });
        return;
      }

      if (hClosing <= limit + EPS) {
        let chosenSplit = null;
        for (const R of bodySplitRowCandidates) {
          const elB = precisionBodyByRRef.current[R];
          if (!elB) continue;
          if (elB.offsetHeight <= limitBodySplit + EPS) {
            chosenSplit = R;
            break;
          }
        }
        if (chosenSplit != null) {
          setA4Preview((prev) => {
            const next = {
              pageH: fullPageH,
              pageCount: 2,
              splitMode: 'body-closing',
              invoiceTableTotalRows: chosenSplit,
            };
            return samePreview(prev, next) ? prev : next;
          });
          return;
        }
      }

      const minR = candidates[candidates.length - 1];
      const root = precisionAllByRRef.current[minR];
      if (!root) return;
      const totalH = root.offsetHeight;
      const pageCount = Math.max(1, Math.ceil(totalH / fullPageH - 0.0001));
      setA4Preview((prev) => {
        const next = {
          pageH: fullPageH,
          pageCount,
          splitMode: null,
          invoiceTableTotalRows: minR,
        };
        return samePreview(prev, next) ? prev : next;
      });
      return;
    }
  }, [invoiceRowCandidates, bodySplitRowCandidates, effectiveRowCount, isPrecisionPaymentLike, paperSize, dataListExportContent]);

  // Ne pas dépendre de normalizedFormData (nouveau literal à chaque render → boucle infinie).
  // Le ResizeObserver suffit quand le contenu change; documentType + selectedTemplateId = changement de modèle.
  useLayoutEffect(() => {
    const root = a4PrecisionMeasureWrapRef.current;
    if (!root) {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        measureA4Pages();
      });
    });
    ro.observe(root);
    requestAnimationFrame(() => {
      measureA4Pages();
    });
    return () => {
      ro.disconnect();
    };
  }, [measureA4Pages, documentType, selectedTemplateId, paperSize]);

  /**
   * Aperçu : la souris peut être au-dessus la zone PDF alors que la barre qui défile est le `<main>` du Layout.
   * Un wrapper avec overflow auto + sans débordement réel peut « manger » la molette sans remonter le scroll au parent.
   * On transfère explicitement deltaY vers le scroll du main (_listener non passive pour pouvoir preventDefault).
   */
  useEffect(() => {
    const root = documentRef.current;
    if (!root) return undefined;

    const onWheel = (e) => {
      const scrollHost = root.closest('.main-content') || root.closest('main');
      if (!scrollHost) return;
      const maxScrollTop = Math.max(0, scrollHost.scrollHeight - scrollHost.clientHeight);
      if (maxScrollTop <= 0) return;

      const delta = e.deltaY;
      if (delta > 0 && scrollHost.scrollTop >= maxScrollTop - 1) return;
      if (delta < 0 && scrollHost.scrollTop <= 0) return;

      scrollHost.scrollTop += delta;
      e.preventDefault();
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [documentType, selectedTemplateId]);

  const isListExport = dataListExportContent != null;
  const isEmbeddedListExportPage = isPrintExport && listExportEmbedMode;

  return (
    <div 
      data-paper-size={paperSize}
      data-print-export={isPrintExport ? 'true' : undefined}
      style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isPrintExport ? 0 : '2rem',
          background: 'transparent',
          minHeight: isPrintExport ? 'auto' : '100vh',
          maxWidth: '100%',
          overflowX: isPrintExport ? 'visible' : 'auto',
          overflowY: 'visible',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'auto',
          zIndex: 1,
          gap: isPrintExport ? 0 : '2rem'
        }} 
        ref={documentRef}
        onDragOver={isEmbeddedListExportPage ? undefined : handleDragOver}
        onDrop={isEmbeddedListExportPage ? undefined : handleDrop}
        onDragLeave={isEmbeddedListExportPage ? undefined : handleDragLeave}
        className={isEmbeddedListExportPage ? 'qawaem-list-export-page-sheet' : 'document-container scrollbar-hide'}
      >
    {/* 🎨 Template caché pour mesure uniquement */}
        {!isEmbeddedListExportPage && (
        <div
          ref={a4PrecisionMeasureWrapRef}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            width: paperDimensions.width,
            boxSizing: 'border-box',
          }}
        >
          {bodySplitRowCandidates.map((R) => (
            <React.Fragment key={`prec-measure-${R}`}>
              {invoiceRowCandidateSet.has(R) && (
                <div ref={bindPrecisionAllRowRef(R)}>
                  <VisualTemplate
                    documentType={documentType}
                    formData={normalizedFormData}
                    clientData={clientDataNormalized}
                    partnerType={partnerType}
                    paperSize={paperSize}
                    settings={settings}
                    products={products}
                    totalsRecapRows={totalsRecapRows}
                    documentPartnerLabel={documentPartnerLabel}
                    documentDetailsLabel={documentDetailsLabel}
                    warehouseOriginData={warehouseOriginData}
                    warehouseDestinationData={warehouseDestinationData}
                    exitNoteData={exitNoteData}
                    shippingSlipData={shippingSlipData}
                    headerDetailRows={headerDetailRows}
                    documentDetailRows={documentDetailRows}
                    documentInfoSections={documentInfoSections}
                    supplementaryInfo={supplementaryInfo}
                    bonPourInfo={bonPourInfo}
                    etablieParInfo={etablieParInfo}
                    recueParInfo={recueParInfo}
                    invoiceRenderPart="all"
                    itemsTableTotalRows={R}
                    compactNotesSignatureSpacing={compactNotesSignatureSpacing}
                  />
                </div>
              )}
              <div ref={bindPrecisionBodyRowRef(R)}>
                <VisualTemplate
                  documentType={documentType}
                  formData={normalizedFormData}
                  clientData={clientDataNormalized}
                  partnerType={partnerType}
                  paperSize={paperSize}
                  settings={settings}
                  products={products}
                  totalsRecapRows={totalsRecapRows}
                  documentPartnerLabel={documentPartnerLabel}
                  documentDetailsLabel={documentDetailsLabel}
                  headerDetailRows={headerDetailRows}
                  documentDetailRows={documentDetailRows}
                  documentInfoSections={documentInfoSections}
                  supplementaryInfo={supplementaryInfo}
                  bonPourInfo={bonPourInfo}
                  etablieParInfo={etablieParInfo}
                  recueParInfo={recueParInfo}
                  invoiceRenderPart="body"
                  itemsTableTotalRows={R}
                />
              </div>
            </React.Fragment>
          ))}
          <div ref={precisionClosingMeasureRef}>
            <VisualTemplate
              documentType={documentType}
              formData={normalizedFormData}
              clientData={clientDataNormalized}
              partnerType={partnerType}
              paperSize={paperSize}
              settings={settings}
              products={products}
              totalsRecapRows={totalsRecapRows}
              documentPartnerLabel={documentPartnerLabel}
              headerDetailRows={headerDetailRows}
              documentDetailRows={documentDetailRows}
              documentInfoSections={documentInfoSections}
              supplementaryInfo={supplementaryInfo}
              bonPourInfo={bonPourInfo}
              etablieParInfo={etablieParInfo}
              recueParInfo={recueParInfo}
              invoiceRenderPart="closing"
            />
          </div>
        </div>
        )}
        
    {/* 🎨 Pages A4 physiquement séparées - fond blanc fourni par wrapper */}
        {a4Preview.pageCount > 0 && Array.from({ length: a4Preview.pageCount }, (_, pageIdx) => {
          const company = settings?.company || {};
          const companyAddress = company?.address || '';
          const companyPhone = company?.phone || '';
          const companyEmail = company?.email || '';
          const companyWebsite = company?.website || '';
          
          // Charger les comptes bancaires
          const bankAccounts = [];
          if (company?.bankAccountsArray && Array.isArray(company.bankAccountsArray)) {
            company.bankAccountsArray.forEach(acc => {
              if (acc.account && acc.bank) {
                bankAccounts.push(`${acc.account} ${acc.bank} ${acc.region || acc.wilaya || ''}`);
              }
            });
          } else {
            if (company?.bankAccount1) bankAccounts.push(company.bankAccount1);
            if (company?.bankAccount2) bankAccounts.push(company.bankAccount2);
            if (company?.bankAccount3) bankAccounts.push(company.bankAccount3);
          }
          
          // Charger les comptes CCP
          const ccpAccountsData = [];
          if (company?.ccpAccountsArray && Array.isArray(company.ccpAccountsArray)) {
            company.ccpAccountsArray.forEach(acc => {
              if (acc.account) {
                ccpAccountsData.push(acc);
              }
            });
          } else if (company?.bankAccount4) {
            const parts = company.bankAccount4.trim().split(/\s+/);
            ccpAccountsData.push({
              account: parts[0] || '',
              raisonSociale: '',
              region: parts[2] || ''
            });
          }
          
          const bankNames = {
            SNB: 'SNB', RJHI: 'RJHI', RIBL: 'RIBL', INMA: 'INMA', SAIB: 'SAIB',
            BSFR: 'BSFR', BLAD: 'BLAD', SABB: 'SABB', BJAZ: 'BJAZ', ANB: 'ANB',
          };
          const parsedBankAccounts = bankAccounts.map(acc => {
            const parts = acc.trim().split(/\s+/);
            const accountNum = parts[0] || '';
            const bankCode = parts[1] || '';
            const region = parts[2] || '';
            const bankFullName = bankNames[bankCode] || bankCode;
            return { accountNum, bankName: bankFullName, region: region ? region.toUpperCase() : '' };
          }).filter(acc => acc.accountNum);
          
          const parsedCcpAccounts = ccpAccountsData.map(acc => ({
            accountNum: acc.account || '',
            raisonSociale: acc.raisonSociale || '',
            region: (acc.region || acc.wilaya || '').toUpperCase()
          })).filter(acc => acc.accountNum);
          
          const isSplitBodyClosing = a4Preview.splitMode === 'body-closing';
          const invoiceRenderPart = isSplitBodyClosing ? (pageIdx === 0 ? 'body' : 'closing') : 'all';
          const continuationHint = isSplitBodyClosing && pageIdx === 1;
          const fillPageForPage = isSplitBodyClosing ? pageIdx === 1 : true;
          const itemsTableTotalRowsProp = a4Preview.invoiceTableTotalRows;
          const signatureBlockPinToFooter = invoiceRenderPart !== 'closing';
          const compactNotesSigProp = compactNotesSignatureSpacing && invoiceRenderPart === 'all';

          const heritagePageFrame = selectedTemplateId === 'heritage';

          return (
          <div
            key={`a4-page-${pageIdx}`}
            className="invoice-a4-page"
            style={{
              width: paperDimensions.width,
              height: paperDimensions.height,
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.08)',
              borderRadius: '4px',
              overflow: 'visible',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              pageBreakAfter: pageIdx < a4Preview.pageCount - 1 ? 'always' : 'auto',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={
                heritagePageFrame
                  ? {
                      flex: '1 1 auto',
                      minHeight: 0,
                      width: isListExport
                        ? `calc(100% - ${footerSp(32)}px)`
                        : `calc(100% - ${footerSp(40)}px)`,
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      margin: isListExport
                        ? `${footerSp(12)}px auto`
                        : `${footerSp(20)}px auto`,
                      border: '1px solid #e7e3da',
                      overflow: 'hidden',
                    }
                  : { display: 'contents' }
              }
            >
            {/* Zone document : occupe l'espace au-dessus du pied de page (flux flex, pas en overlay) */}
            <div
              style={{
                flex: '1 1 auto',
                position: 'relative',
                overflow: 'hidden',
                minHeight: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {isSplitBodyClosing ? (
                <div
                  style={{
                    minHeight: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                  }}
                >
                  <VisualTemplate 
                    documentType={documentType}
                    formData={normalizedFormData}
                    clientData={clientDataNormalized}
                    partnerType={partnerType}
                    paperSize={paperSize}
                    settings={settings}
                    products={products}
                    totalsRecapRows={totalsRecapRows}
                    documentPartnerLabel={documentPartnerLabel}
                    documentDetailsLabel={documentDetailsLabel}
                    warehouseOriginData={warehouseOriginData}
                    warehouseDestinationData={warehouseDestinationData}
                    exitNoteData={exitNoteData}
                    shippingSlipData={shippingSlipData}
                    headerDetailRows={headerDetailRows}
                    documentDetailRows={documentDetailRows}
                    documentInfoSections={documentInfoSections}
                    supplementaryInfo={supplementaryInfo}
                    bonPourInfo={bonPourInfo}
                    etablieParInfo={etablieParInfo}
                    recueParInfo={recueParInfo}
                    fillPageAboveFooter={fillPageForPage}
                    invoiceRenderPart={invoiceRenderPart}
                    continuationHint={continuationHint}
                    itemsTableTotalRows={itemsTableTotalRowsProp}
                    signatureBlockPinToFooter={signatureBlockPinToFooter}
                    compactNotesSignatureSpacing={compactNotesSigProp}
                    dataListExportContent={dataListExportContent}
                    listExportTitle={listExportTitle}
                    listExportContinuation={listExportContinuation}
                    listExportPageIndex={listExportPageIndex}
                    listExportPageCount={listExportPageCount}
                  />
                </div>
              ) : (
              <div
                style={{
                  position: 'absolute',
                  top: -(pageIdx * a4Preview.pageH),
                  left: 0,
                  width: heritagePageFrame ? '100%' : paperDimensions.width,
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    minHeight: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                  }}
                >
                  <VisualTemplate 
                    documentType={documentType}
                    formData={normalizedFormData}
                    clientData={clientDataNormalized}
                    partnerType={partnerType}
                    paperSize={paperSize}
                    settings={settings}
                    products={products}
                    totalsRecapRows={totalsRecapRows}
                    documentPartnerLabel={documentPartnerLabel}
                    documentDetailsLabel={documentDetailsLabel}
                    warehouseOriginData={warehouseOriginData}
                    warehouseDestinationData={warehouseDestinationData}
                    exitNoteData={exitNoteData}
                    shippingSlipData={shippingSlipData}
                    headerDetailRows={headerDetailRows}
                    documentDetailRows={documentDetailRows}
                    documentInfoSections={documentInfoSections}
                    supplementaryInfo={supplementaryInfo}
                    bonPourInfo={bonPourInfo}
                    etablieParInfo={etablieParInfo}
                    recueParInfo={recueParInfo}
                    fillPageAboveFooter
                    itemsTableTotalRows={itemsTableTotalRowsProp}
                    signatureBlockPinToFooter={signatureBlockPinToFooter}
                    compactNotesSignatureSpacing={compactNotesSigProp}
                    dataListExportContent={dataListExportContent}
                    listExportTitle={listExportTitle}
                    listExportContinuation={listExportContinuation}
                    listExportPageIndex={listExportPageIndex}
                    listExportPageCount={listExportPageCount}
                  />
                </div>
              </div>
              )}
            </div>
            
            {/* 🎯 Pied de page : dans le flux en bas de page (plus de « trou » blanc au-dessus) */}
            {/* Templates with their own footer design: pulse (gray), zenith (dark purple), heritage (simple line), others (black) */}
            <div className="invoice-document-footer" style={{
              flexShrink: 0,
              boxSizing: 'border-box',
              background: selectedTemplateId === 'heritage' 
                ? 'transparent'
                : selectedTemplateId === 'zenith' 
                  ? '#0f1f3d'
                  : selectedTemplateId === 'corporate'
                    ? 'transparent'
                    : selectedTemplateId === 'pulse' ? '#f8f9fa' : '#1a1a1a',
              color: selectedTemplateId === 'heritage' 
                ? '#7d735f'
                : selectedTemplateId === 'corporate'
                  ? '#334155'
                  : selectedTemplateId === 'pulse' 
                    ? '#6c757d' 
                    : 'white',
              /* Équilibre : un peu d’air haut/bas sans bande trop « vide » ; bas un peu plus haut que le haut pour l’impression */
              paddingTop: `${footerSp(12)}px`,
              paddingRight: isListExport ? `${footerSp(24)}px` : `${footerSp(52)}px`,
              paddingBottom: `${footerSp(22)}px`,
              paddingLeft: isListExport ? `${footerSp(24)}px` : `${footerSp(52)}px`,
              fontSize: selectedTemplateId === 'heritage' ? footerFs(10) : footerFs(11),
              textAlign: 'center',
              marginTop: `${footerSp(8)}px`,
              direction: isListExport ? 'rtl' : 'ltr',
              borderTop: selectedTemplateId === 'heritage' 
                ? '1px solid #e7e3da' 
                : selectedTemplateId === 'corporate'
                  ? '1px solid #cbd5e1'
                  : 'none',
              fontFamily: selectedTemplateId === 'heritage' 
                ? '"Cormorant Garamond", serif'
                : selectedTemplateId === 'cube' 
                  ? '"Sora", sans-serif'
                  : selectedTemplateId === 'precision' || selectedTemplateId === 'corporate'
                    ? '"Inter", sans-serif'
                    : 'system-ui, -apple-system, sans-serif',
              boxShadow: 'none',
              backgroundImage: selectedTemplateId === 'corporate'
                ? 'radial-gradient(rgba(228, 233, 242, 0.3) 1px, transparent 1px)'
                : 'none',
              backgroundSize: selectedTemplateId === 'corporate'
                ? '10px 10px'
                : 'auto',
            }}>
              {companyAddress && (
                <div style={{ 
                  color: selectedTemplateId === 'heritage' 
                    ? '#7d735f' 
                    : selectedTemplateId === 'corporate'
                      ? '#475569'
                      : selectedTemplateId === 'pulse' ? '#6c757d' : selectedTemplateId === 'zenith' ? 'white' : '#aaaaaa', 
                  marginBottom: `${footerSp(5)}px`, 
                  fontSize: footerFs(11),
                  maxWidth: '100%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  paddingLeft: `${footerSp(4)}px`,
                  paddingRight: `${footerSp(4)}px`,
                }}>
                  <strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.headquartersAddress}</strong> {companyAddress}
                </div>
              )}
              <div style={{ 
                color: selectedTemplateId === 'heritage' 
                  ? '#7d735f' 
                  : selectedTemplateId === 'corporate'
                    ? '#475569'
                    : selectedTemplateId === 'pulse' ? '#6c757d' : selectedTemplateId === 'zenith' ? 'white' : '#aaaaaa', 
                marginBottom: `${footerSp(5)}px`, 
                fontSize: footerFs(11),
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'baseline',
                columnGap: `${footerSp(10)}px`,
                rowGap: `${footerSp(5)}px`,
                maxWidth: '100%',
                paddingLeft: `${footerSp(4)}px`,
                paddingRight: `${footerSp(4)}px`,
              }}>
                {companyPhone && <span><strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.phone}:</strong> {companyPhone}</span>}
                {companyPhone && companyEmail && <span style={{ opacity: 0.65, margin: `0 ${footerSp(4)}px` }}>|</span>}
                {companyEmail && <span><strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.email}:</strong> {companyEmail}</span>}
                {companyEmail && companyWebsite && <span style={{ opacity: 0.65, margin: `0 ${footerSp(4)}px` }}>|</span>}
                {companyWebsite && <span><strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.website}</strong> {companyWebsite}</span>}
              </div>
              {(parsedBankAccounts.length > 0 || parsedCcpAccounts.length > 0) && (
                <div style={{ 
                  fontSize: footerFs(9), 
                  color: selectedTemplateId === 'heritage' 
                    ? '#7d735f' 
                    : selectedTemplateId === 'corporate'
                      ? '#64748b'
                      : selectedTemplateId === 'pulse' ? '#6c757d' : selectedTemplateId === 'zenith' ? 'white' : '#aaaaaa', 
                  lineHeight: '1.32',
                  marginTop: `${footerSp(5)}px`,
                  maxWidth: '100%',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  paddingLeft: `${footerSp(4)}px`,
                  paddingRight: `${footerSp(4)}px`,
                }}>
                  {parsedBankAccounts.length > 0 && (
                    <>
                      <strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.bankAccountsHeader}</strong>{' '}
                      {parsedBankAccounts.map((acc, idx) => (
                        <span key={idx}>
                          {acc.accountNum} <strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{acc.bankName}</strong> {acc.region}
                          {idx < parsedBankAccounts.length - 1 ? ' / ' : ''}
                        </span>
                      ))}
                    </>
                  )}
                  {parsedBankAccounts.length > 0 && parsedCcpAccounts.length > 0 && <span style={{ margin: `0 ${footerSp(8)}px` }}>|</span>}
                  {parsedCcpAccounts.length > 0 && (
                    <>
                      <strong style={{ color: selectedTemplateId === 'heritage' ? '#3a3025' : 'inherit' }}>{PREVIEW_LABELS.ccpAccountHeader}</strong>{' '}
                      {parsedCcpAccounts.map((acc, idx) => (
                        <span key={idx}>
                          {acc.accountNum}{acc.raisonSociale && ` ${acc.raisonSociale}`} {acc.region}
                          {idx < parsedCcpAccounts.length - 1 ? ' / ' : ''}
                        </span>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>
        );
        })}
        
        {/* Styles CSS pour l'impression A4 */}
        {!isPrintExport && (
        <style>{`
          @media print {
            .document-container {
              padding: 0 !important;
              gap: 0 !important;
              display: block !important;
            }
            .invoice-a4-page {
              box-shadow: none !important;
              border-radius: 0 !important;
              margin: 0 !important;
              page-break-after: always !important;
              page-break-inside: avoid !important;
            }
            .invoice-a4-page:last-child {
              page-break-after: auto !important;
            }
            .invoice-document-footer {
              padding-bottom: 6mm !important;
              box-sizing: border-box !important;
            }
            .invoice-a4-page > div[style*="position: absolute"] > div {
              page-break-inside: avoid !important;
            }
          }
          @page {
            size: A4 portrait;
            margin: 5mm 6mm 8mm 6mm;
          }
        `}</style>
        )}
        
        {/* Draggable Stamp */}
        {settings.company?.cachet && !isRealTimePreview && !isPrintExport && (
          <div 
            style={{
              position: 'absolute',
              left: `${stampPosition.x}px`,
              top: `${stampPosition.y}px`,
              width: `${stampSize + 4}px`,
              height: `${stampSize + 4}px`,
              zIndex: 1000,
              display: 'block',
              border: '1px dashed rgba(0, 123, 255, 0.2)',
              borderRadius: '4px',
              padding: '2px'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-25px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }}>
              <button 
                onClick={() => adjustSize('stamp', -10)} 
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '14px'
                }}
              >-</button>
              <span>{stampSize}px</span>
              <button 
                onClick={() => adjustSize('stamp', 10)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '14px'
                }}
              >+</button>
            </div>
            <img 
              src={settings.company.cachet} 
              alt={PREVIEW_LABELS.companyStampAlt} 
              draggable={true}
              onDragStart={(e) => handleDragStart(e, 'stamp')}
              style={{ 
                width: `${stampSize}px`,
                height: `${stampSize}px`,
                cursor: 'move',
                display: 'block',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
        
        {/* Draggable Signature */}
        {settings.company?.signature && !isRealTimePreview && (
          <div 
            style={{
              position: 'absolute',
              left: `${signaturePosition.x}px`,
              top: `${signaturePosition.y}px`,
              width: `${signatureSize.width + 4}px`,
              height: `${signatureSize.height + 4}px`,
              zIndex: 1000,
              display: 'block',
              border: '1px dashed rgba(0, 123, 255, 0.2)',
              borderRadius: '4px',
              padding: '2px'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-25px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }}>
              <button 
                onClick={() => adjustSize('signature', -10)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '14px'
                }}
              >-</button>
              <span>{signatureSize.width}x{signatureSize.height}</span>
              <button 
                onClick={() => adjustSize('signature', 10)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '14px'
                }}
              >+</button>
            </div>
            <img 
              src={settings.company.signature} 
              alt={PREVIEW_LABELS.companySignatureAlt} 
              draggable={true}
              onDragStart={(e) => handleDragStart(e, 'signature')}
              style={{ 
                width: `${signatureSize.width}px`,
                height: `${signatureSize.height}px`,
                cursor: 'move',
                display: 'block',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
    </div>
  );
};

export default TemplateWrapper;




